/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * MySQL / MariaDB dialect-native resolver for the `mysql` engine (PRD Slice
 * "MySQL Resolver — Core").
 *
 * Pipeline mirrors `pgsqlResolver.ts` (pure-text after GRA-7 Slice A4 — no
 * tree-sitter parse in the resolver hot path; chosen template because MySQL
 * also carries columns inline on the bus snapshot — no per-table column
 * cache):
 *   1. Derive `CompletionContext` from source text (statement isolation +
 *      paren-frame state machine + dotted-base extractor + alias/CTE
 *      bindings).
 *   2. Bail when inside a string or comment.
 *   3. Build `CompletionCandidate[]` from:
 *        - alias-dot column expansion (when the dotted base resolves to a
 *          bound alias),
 *        - database-qualified table list (when the base resolves to a
 *          database name — MySQL's `database.table` form),
 *        - position-aware columns + tables + databases + CTEs + keywords +
 *          MySQL built-in functions.
 *   4. Rank via `rankCandidates` with the keyword-casing hint.
 *   5. Translate each `MonacoCompletionItem` to Monaco's runtime shape.
 *
 * MySQL specifics:
 *   - **Identifier quoting** uses backticks (`` `t` ``). Double-quoted form
 *     `"t"` is identifier-only when `@@sql_mode` contains `ANSI_QUOTES`. The
 *     resolver reads the cached `sqlMode` to decide. Output always uses
 *     backticks because they are the canonical / always-safe form.
 *   - **Identifier folding** is case-insensitive — MySQL stores table names
 *     lower-cased on most installs (`@@lower_case_table_names` ∈ {1, 2}).
 *     The fold is applied to unquoted identifiers; quoted forms preserve
 *     case verbatim (matches `mysqlIdentifiers.foldIdentifier`).
 *   - **Schema vs. database**: MySQL collapses the two — the `database` field
 *     is the only namespace. The bus snapshot only carries the current
 *     connection's database; cross-database table completion is out of
 *     scope until per-database introspection lands. Other databases surface
 *     as schema-kind candidates so the user sees the namespaces they can
 *     dot into, even when the tables themselves aren't in the snapshot.
 *
 * Pure module — no Monaco/React imports at module-level. The `monaco`
 * runtime instance flows through resolver input.
 */

import type {
  AliasBinding,
  ColumnMeta,
  CompletionCandidate,
  CompletionCandidateTier,
  CompletionPositionKind,
  CteBinding,
  DatabaseEngineId,
  FkEdge,
  TableMeta,
} from '@/core/engines';
import { ensureMysqlSchema, getCachedMysqlSchema } from '@/features/connections/services/language-services/mysqlSchemaCache';
import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { deriveCompletionContext } from '@/features/intellisense/context/deriveContext';
import { applyGroupByScopeFilter } from '@/features/intellisense/context/groupByScope';
import { isAfterCompletedFromRelation } from '@/features/intellisense/context/isAfterCompletedFromRelation';
import { isAfterCompletedSelectProjection } from '@/features/intellisense/context/isAfterCompletedSelectProjection';
import { scanOptionsFor } from '@/features/intellisense/context/slotTextScan';
import { applyParenScopeFilter } from './parenScopeFilter';
import { applyIndexTargetFilter } from './indexTargetFilter';
import { emitIndexNameCandidates } from './indexNameSlot';
import { filterAlreadyAssignedSetColumns } from './filterAssignedSetColumns';
import { emitAggregateArgHints, emitAggregateTailHints } from './aggregateArgHints';
import { emitProjectionWildcard } from './projectionWildcard';
import { emitSavepointNameCandidates } from '../context/savepointSlot';
import { isAtRenameTargetSlot } from '../context/renameTargetSlot';
import { emitDeclaredNameSlotCandidates } from '../context/declaredNameSlot';
import { isAtDeclareTypeSlot } from '../context/declareTypeSlot';
import { emitTriggerPseudoRelationNames } from '../resolver/pseudoTables';
import { isAtProceduralStatementStart } from '@/features/intellisense/context/proceduralStatementStart';
import { isInsideRoutineBodyBlock } from '@/features/intellisense/context/insideRoutineBody';
import { isAtJoinTargetSlot } from '@/features/intellisense/context/isAtJoinTargetSlot';
import {
  classifyNextKeywordSlot,
  type NextKeywordSlotResult,
} from '@/features/intellisense/context/nextKeywordSlot';
import { getLegalNextKeywords, isRowLimitSlot } from '@/features/intellisense/context/legalNextKeywords';
import {
  emitCompoundKeywordRemainders,
  keywordLabelsAlreadyOffered,
} from './compoundKeywordRemainder';
import { buildRowLimitOperandCandidates } from './rowLimitOperand';
import { inferSubqueryColumns } from '@/features/intellisense/resolver/inferSubqueryColumns';
import { emitNamedWindowCandidates } from '@/features/intellisense/resolver/inferNamedWindows';
import { resolveCteColumns } from '@/features/intellisense/resolver/resolveCteColumns';
import { emitSelectListAliasCandidates } from '@/features/intellisense/resolver/selectListAliases';
import {
  generateFkJoinSnippet,
  makeApplicableFksFinder,
} from '@/features/intellisense/fkJoin/fkJoinSnippet';
import { makeFkEndpointMatcher } from '@/features/intellisense/fkJoin/fkEndpointMatch';
import {
  computeBufferReferencedTableKeys,
  computeFkLinkedTableKeys,
  fkLinkedTableKey,
  unionTableKeys,
} from '@/features/intellisense/fkJoin/fkLinkedTableKeys';
import { promoteCaretBindingRelation } from '@/features/intellisense/fkJoin/caretBindingPromotion';
import { rankCandidates, buildRankOptions } from '@/features/intellisense/ranking/rank';
import { emitAliasCandidate } from './aliasSuggestions';
import { enrichCandidatesWithDocs } from './completionDocs';
import { toMonacoItem } from './monacoItem';

import type {
  OrchestratorResolver,
  OrchestratorResolverInput,
  OrchestratorResolverResult,
} from '../orchestrator/types';

import { computeClauseContext } from '@/features/sql-grammar/document/editorContextClause';
import { emitCreateObjectNamespaces, isCreateIndexNameSlot } from './createObjectNamespaces';
import {
  backtickQuote,
  foldIdentifier,
  formatIdentifier,
  formatQualifiedTwoPart,
  needsBacktickQuote,
  unquoteIdentifier,
  type MysqlQuotingOptions,
} from './mysqlIdentifiers';
import { getMysqlKeywordsForPosition, MYSQL_KEYWORDS } from './mysqlKeywords';
import { getMysqlCastTargetTypes, getMysqlDataTypes } from './mysqlDataTypes';
import { getMysqlBuiltinFunctions } from './mysqlBuiltinFunctions';
import { cteSupportedForServerVersion } from './mysqlVersionGate';
import {
  detectMatchAgainstContext,
  findInsertNewAlias,
  findInsertTarget,
  findUserVariables,
  getMysqlSystemVariables,
  isMariaDb,
  isMysqlSystemDatabase,
  isMysqlMultiTableDeleteAliasSlot,
  MYSQL_SYSTEM_DATABASES,
  type MysqlInsertNewAlias,
  type MysqlInsertTarget,
} from './mysqlExtras';
import { isInsideGenerationExpression, isInsideTableDdlStatement } from './ddlStatementScan';
import { emitValueSlotCandidates } from '../valueSlot/emitValueSlotCandidates';
import { emitPredicateHeadCandidates } from '../predicateHead/emitPredicateHeadCandidates';
import { buildMysqlValueSlotAdapter, collectMysqlScalarRoutineDescriptors } from './mysqlValueSlotAdapter';
import {
  emitResolvedValueCandidates,
  legalValueKeywordsFor,
  type ResolvedValueTarget,
} from '../valueSlot/emitResolvedValueCandidates';
import { coarseTypeOfDataType } from '../valueSlot/types';
import { findInsertValuesTarget } from '../context/findInsertValuesTarget';
import { emitTypeAwareOperators, emitTypeAwareExpressionOperators } from '../operators/emitJsonOperatorCandidates';
import { findPrecedingExpressionType } from '../operators/findPrecedingExpressionType';
import { makeRoutineReturnResolver } from '../operators/routineReturnType';
import {
  emitJsonPathCandidates,
  findJsonPathStringPosition,
} from '../operators/emitJsonPathCandidates';
import { findPrecedingColumnOperand } from '../operators/findPrecedingColumnOperand';
import { emitEnumValueCandidates } from '../operators/emitEnumValueCandidates';
import { parseInlineEnumMembers } from '../operators/parseInlineEnumMembers';
import { detectFindInSetColumnArg } from '../operators/detectFindInSetColumn.mysql';
import { computeEnumValueEdit } from '../operators/enumValueInsertion';
import { extractJsonPathFromCursorMysql } from '../operators/extractJsonPathFromCursor.mysql';
import { findValueStringBodyStart } from '../context/comparisonScanHelpers';
import { detectComparisonValueContext } from '../context/detectComparisonValueContext';
import { findTypedInsertColumns } from '../context/findTypedInsertColumns';
import { isAtCallStmtRoutineRef } from '../context/isAtCallStmtRoutineRef';
import { boundTablesFirst, resolveColumnRefToTable, type RelationResolver } from '../context/resolveColumnRefToTable';
import { getPathHints } from '@/features/sql-resolver/json/pathHints';
import {
  getJsonPathHints,
  isJsonColumnDeclared,
  getLooseRelationships,
} from '@/features/sql-resolver/metaInfoHints/metaInfoHintProvider';
import { emitLooseRelJoinSnippets } from '@/features/intellisense/fkJoin/looseFkJoinSnippet';
import { applyJoinOnColumnRanking } from '@/features/intellisense/fkJoin/fkColumnRanking';
import {
  applyJsonFunctionArgPromotion,
  collectBufferJsonFunctionColumns,
  isAtJsonFunctionFirstArg,
} from '@/features/intellisense/fkJoin/jsonFunctionArgAffinity';
import { isAtInsertTargetSlot } from '@/features/intellisense/context/isAtInsertTargetSlot';
import {
  applyReferencesTargetPromotion,
  findFkSourceColumnStems,
} from '@/features/intellisense/fkJoin/referencesTargetInference';
import {
  findTriggerHostTable,
  type TriggerHostTable,
} from '@/features/intellisense/resolver/triggerHostTable';
import type { LooseRelationship } from '@/types/electron';
import {
  isInsideMysqlRoutineBody,
  emitMysqlProceduralLocalCandidates,
} from './mysqlProceduralLocals';
import {
  findInBufferCreatedTables,
  type InBufferTable,
} from './mysqlInBufferTables';
import {
  applyInBufferTableOps,
  scanInBufferTableOps,
  findInBufferCreatedViews,
  type InBufferRename,
} from '../context/inBufferTables';

const ENGINE: DatabaseEngineId = 'mysql';

// F5.2-CB1.7 — position-aware keyword promotion. When the cursor lands at
// a sub-position where a specific continuation keyword is the most likely
// next token, promote those keywords to tier 1 so they outrank schema
// candidates and the rest of the curated keyword list (which stays at
// tier 7). The `.filter()` guard against `MYSQL_KEYWORDS` keeps these sets
// honest — entries that aren't in the curated emit list are silently
// dropped here too, so a typo or future trim of the keyword list cannot
// produce a promoted candidate that never actually emits.
//
// After `SELECT <projection-complete>` → FROM / set-op continuation.
// INTERSECT / EXCEPT are MySQL 8.0.31+ — they may or may not be in the
// curated list; the filter handles both cases.
// MySQL multi-token operators at WHERE/HAVING position. `IS NULL` /
// `IS NOT NULL` are two-token operators the curated single-keyword emitter
// cannot express; `NOT EXISTS` is the negated-subquery-existence operator
// that pairs with `WHERE … (SELECT …)`. Push them directly when the
// position is `where` or `having`.
const MYSQL_WHERE_NULL_OPERATOR_KEYWORDS: readonly string[] = [
  'IS NULL',
  'IS NOT NULL',
  'NOT EXISTS',
];

// MySQL JSON functions whose first argument names the JSON document column —
// drives the JSON-arg affinity lift at `JSON_EXTRACT(p.▮` (parity with the
// oracle JSON_VALUE surface). Case-folded by the affinity regex.
const MYSQL_JSON_FN_NAMES: readonly string[] = [
  'JSON_EXTRACT',
  'JSON_UNQUOTE',
  'JSON_KEYS',
  'JSON_CONTAINS',
  'JSON_CONTAINS_PATH',
  'JSON_LENGTH',
  'JSON_DEPTH',
  'JSON_TYPE',
  'JSON_VALID',
  'JSON_VALUE',
  'JSON_TABLE',
  'JSON_SEARCH',
  'JSON_OVERLAPS',
  'JSON_PRETTY',
];

const MYSQL_NEXT_AFTER_SELECT_PROJECTION: ReadonlySet<string> = new Set(
  ['FROM', 'UNION', 'INTERSECT', 'EXCEPT'].filter((k) => MYSQL_KEYWORDS.has(k)),
);

// After `FROM <relation-complete>` → WHERE / join continuation / shaping
// clauses. These mirror the MSSQL resolver's set, narrowed to MySQL's
// curated keyword surface (no APPLY / PIVOT / etc.).
const MYSQL_NEXT_AFTER_FROM_RELATION: ReadonlySet<string> = new Set(
  [
    'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'OUTER', 'CROSS',
    'ON', 'USING', 'AS', 'ORDER', 'GROUP', 'HAVING', 'UNION',
    'LIMIT', 'OFFSET', 'FOR',
  ].filter((k) => MYSQL_KEYWORDS.has(k)),
);

/**
 * F5.3 Wave-4 — MySQL `CREATE [UNIQUE|FULLTEXT|SPATIAL] INDEX |` index-name
 * slot. MySQL 8.0.29+ accepts `IF NOT EXISTS` here; surface it so the
 * keyword doesn't get drowned by tables when the resolver classifies the
 * slot as `'unknown'`.
 */
const MYSQL_CREATE_INDEX_NAME_RE =
  /\bCREATE\s+(?:UNIQUE\s+|FULLTEXT\s+|SPATIAL\s+)?INDEX\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

/**
 * Position kinds where MySQL procedural locals / parameters are NOT valid
 * completion candidates — FROM table list and the DDL slots that take
 * type / column / constraint identifiers (not expressions). At expression
 * positions (WHERE / HAVING / SET / SELECT-list / etc.) procedural locals
 * remain visible.
 */
function isNonExpressionSlot(positionKind: CompletionPositionKind | undefined): boolean {
  if (!positionKind) return false;
  return (
    positionKind === 'from' ||
    positionKind.startsWith('ddl-')
  );
}

function isMysqlCreateIndexNamePosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return MYSQL_CREATE_INDEX_NAME_RE.test(slice);
}

/**
 * F5.3 Wave-4 — MySQL stored-routine characteristic-clause slot. Inside a
 * `CREATE [DEFINER=…] [OR REPLACE] {FUNCTION|PROCEDURE} name(…)
 * [RETURNS type]  <characteristics>  BEGIN …`, the slot between the
 * closing `)` of the parameter list (or the optional RETURNS type) and
 * the BEGIN body accepts any of the 10 routine-characteristic keywords
 * documented in the MySQL CREATE FUNCTION reference.
 *
 * Detector strategy: cursor's enclosing statement starts with the CREATE
 * FUNCTION/PROCEDURE header, the slice contains the closing `)` of the
 * routine's parameter list, and no BEGIN has appeared yet.
 */
const MYSQL_ROUTINE_CHARACTERISTIC_KEYWORDS: readonly string[] = [
  'DETERMINISTIC',
  'NOT DETERMINISTIC',
  'LANGUAGE SQL',
  'CONTAINS SQL',
  'NO SQL',
  'READS SQL DATA',
  'MODIFIES SQL DATA',
  'SQL SECURITY DEFINER',
  'SQL SECURITY INVOKER',
  'COMMENT',
];

function isMysqlRoutineCharacteristicPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  // Must be inside a CREATE FUNCTION / PROCEDURE header.
  if (
    !/\bCREATE\s+(?:DEFINER\s*=\s*\S+\s+)?(?:OR\s+REPLACE\s+)?(?:FUNCTION|PROCEDURE)\b/i.test(
      slice,
    )
  ) {
    return false;
  }
  // BEGIN signals we've moved into the body — characteristic slot is closed.
  if (/\bBEGIN\b/i.test(slice)) return false;
  // Find the index of the last `)` — must be present (closes the parameter
  // list) and the cursor must sit past it.
  const lastRparen = slice.lastIndexOf(')');
  if (lastRparen === -1) return false;
  // After the `)` the cursor's tail is the characteristic slot — i.e.
  // optional RETURNS clause, possibly some characteristic keywords typed,
  // optionally a partial keyword. The only restriction is "no semicolon
  // between `)` and cursor" — once a `;` appears we're outside the statement.
  const tail = slice.slice(lastRparen + 1);
  if (tail.includes(';')) return false;
  return true;
}

/**
 * F5.3 Wave-4 — MySQL window-frame slot detectors. MySQL 8 supports
 * `ROWS|RANGE BETWEEN <start> AND <end>` (no GROUPS). The grammar classifier
 * returns `'unknown'` inside the frame clause; text-walk regex is the gate.
 */
const MYSQL_FRAME_START_RE =
  /\b(?:ROWS|RANGE)\s+BETWEEN\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const MYSQL_FRAME_START_KEYWORDS: readonly string[] = [
  'UNBOUNDED PRECEDING',
  'CURRENT ROW',
];

const MYSQL_FRAME_END_RE =
  /\b(?:ROWS|RANGE)\s+BETWEEN\s+(?:UNBOUNDED\s+(?:PRECEDING|FOLLOWING)|\d+\s+(?:PRECEDING|FOLLOWING)|CURRENT\s+ROW)\s+AND\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const MYSQL_FRAME_END_KEYWORDS: readonly string[] = [
  'UNBOUNDED FOLLOWING',
  'CURRENT ROW',
];

/** True at MySQL's `CAST(expr AS ▮` / `CONVERT(expr, ▮` type target. Those sit
 *  in a NAMED clause, which the slot seam may not override, so MySQL alone
 *  flooded there while the other four engines had their own surface walks. */
function isMysqlCastTargetPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  // The argument may itself contain a call (`CAST(IFNULL(a, 0) AS ▮`), and
  // MySQL's CONVERT needs no space after the comma (`CONVERT(x,▮`). The
  // sub-pattern allows ONE nested paren group but never a `)` that closes the
  // CAST itself — without that bound, `CAST(x AS SIGNED) AS ▮` (a column alias)
  // and `CONVERT(a, CHAR), b, ▮` (the next projection) both matched.
  return /\b(?:CAST\s*\((?:[^()]|\([^()]*\))*\bAS\s+|CONVERT\s*\((?:[^()]|\([^()]*\))*,\s*)[A-Za-z0-9_]*$/i.test(
    slice,
  );
}

function isMysqlFrameStartPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return MYSQL_FRAME_START_RE.test(slice);
}

function isMysqlFrameEndPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return MYSQL_FRAME_END_RE.test(slice);
}

export const mysqlResolver: OrchestratorResolver = {
  engineId: ENGINE,
  resolve: async (input) => resolveMysql(input),
};

async function resolveMysql(
  input: OrchestratorResolverInput,
): Promise<OrchestratorResolverResult> {
  const { model, position, monaco } = input;
  const text = model.getValue();
  const offset = model.getOffsetAt(position);

  // Script-mode `USE <db>` overrides the connection-default database for
  // snapshot lookup so completion in `USE db1; SELECT * FROM <cursor>` sees
  // db1's tables instead of the tab's connection-default database.
  const scriptScope = input.scriptScope ?? {};
  const ctx = deriveCompletionContext(text, offset, ENGINE, scriptScope);
  const rankOptions = buildRankOptions(text, ctx, offset, input);
  const effectiveDatabase = scriptScope.currentDatabase ?? input.database;
  // F5.3 Wave-4 → plan19 T7 — the fine-grained DDL slot kinds
  // (`ddl-index-column`, `ddl-references-column-list`, `ddl-fk-target`, …) now
  // arrive on `ctx.positionKind` from the token slot seam (`classifyDdlSlot`
  // via `deriveCompletionContext`'s DDL-override gate). The string-walk
  // `classifyDdlPositionKind` override that used to run here is retired.

  const snap = input.connectionId
    ? getSchemaBusSnapshot(input.connectionId, effectiveDatabase)
    : null;

  // JCS Slice C4 — JSON path completion fires even inside a single-quoted
  // string when the string is in a recognised JSON-path position (after `->`,
  // `->>`, `JSON_VALUE(col, `, `JSON_EXTRACT(col, `, etc.). Runs BEFORE the
  // string-bail so the cursor inside `payload -> '|` still gets candidates.
  if (input.connectionId && effectiveDatabase) {
    const nativeEarly = input.connectionId ? getCachedMysqlSchema(input.connectionId) : undefined;
    const ansiQuotesEarly = (nativeEarly?.sqlMode ?? '').toUpperCase().includes('ANSI_QUOTES');
    const pathHintsCandidates = collectMysqlJsonPathCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: effectiveDatabase,
      snapshotTables: snap?.tables ?? [],
      quoting: { ansiQuotes: ansiQuotesEarly },
      aliasBindings: ctx.aliasBindings,
      resolveTable: mysqlTableResolver(snap?.tables ?? [], { ansiQuotes: ansiQuotesEarly }),
    });
    if (pathHintsCandidates.length > 0) {
      const wordInfo = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: wordInfo.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: wordInfo.endColumn,
      };
      const ranked = rankCandidates(pathHintsCandidates, ctx, String(wordInfo.word ?? ''), rankOptions);
      return { suggestions: ranked.map((item) => toMonacoItem(item, monaco, range)) };
    }
  }

  // Phase 3 — enum-value completion. Fires (like JSON-path) BEFORE the
  // string-bail because the cursor sits inside an unclosed string literal at
  // `WHERE status = '|`. Empty cache → 0 candidates → fall through.
  if (input.connectionId && effectiveDatabase) {
    const nativeEarly = input.connectionId
      ? getCachedMysqlSchema(input.connectionId)
      : undefined;
    const ansiQuotesEarly = (nativeEarly?.sqlMode ?? '')
      .toUpperCase()
      .includes('ANSI_QUOTES');
    const enumCandidates = collectMysqlEnumValueCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: effectiveDatabase,
      snapshotTables: snap?.tables ?? [],
      quoting: { ansiQuotes: ansiQuotesEarly },
      aliasBindings: ctx.aliasBindings,
      resolveTable: mysqlTableResolver(snap?.tables ?? [], { ansiQuotes: ansiQuotesEarly }),
    });
    if (enumCandidates.length > 0) {
      const edit = computeEnumValueEdit(model, position, text, offset);
      // CLOSED-string forward extension (script-49-cp-3 fix). When the cursor
      // sits at the body start of a CLOSED string literal — e.g.
      // `IN ('PG', '|PG-13')` with the caret between the opening quote of
      // `'PG-13'` and the `P` — `computeEnumValueEdit` returns a zero-width
      // range at the cursor. Monaco then expands the range forward to the
      // word at the caret (`PG`, since `-` breaks the word) and matches the
      // `PG` enum candidate to that range. Because the candidate's
      // `insertText` (`PG`) equals the text the range already covers
      // (`PG`), Monaco treats the suggestion as a no-op and drops it from
      // the visible list — that is the observed gap: only the literal that
      // is a prefix of the surrounding word goes missing, every other
      // member of the enum surfaces.
      //
      // Fix: when the caret sits inside a CLOSED `'…'` literal whose body
      // extends past the cursor on the same line, widen the replace range
      // forward to cover the entire existing body up to (but not including)
      // the closing quote. The bare insertText then REPLACES `PG-13` with
      // `PG`, never producing a no-op, so Monaco surfaces every enum
      // member regardless of which one happens to be a prefix of the
      // surrounding text.
      const extendedRange = maybeExtendEnumValueRangeForClosedLiteral(
        text,
        offset,
        edit.range,
        position,
      );
      const ranked = rankCandidates(enumCandidates, ctx, edit.word, rankOptions);
      return {
        suggestions: ranked.map((item) => toMonacoItem(item, monaco, extendedRange)),
      };
    }
  }

  if (ctx.insideStringOrComment) return { suggestions: [] };

  const wordInfo = model.getWordUntilPosition(position);
  const typed = String(wordInfo.word ?? '');

  // Cold cache → warm fire-and-forget. Subsequent keystrokes will see the
  // populated snapshot. Same fire-and-forget contract MSSQL / PG use.
  if (!snap && input.connectionId && effectiveDatabase) {
    void ensureMysqlSchema({
      connectionId: input.connectionId,
      database: effectiveDatabase,
    }).catch(() => undefined);
  }

  // MySQL session-variable hints — read from the native cache (the bus
  // snapshot doesn't carry sql_mode / lower_case_table_names today). Cold
  // cache returns undefined; the resolver defaults to backtick-only quoting,
  // which is correct in every sql_mode.
  const native = input.connectionId ? getCachedMysqlSchema(input.connectionId) : undefined;
  const ansiQuotes = (native?.sqlMode ?? '').toUpperCase().includes('ANSI_QUOTES');
  const quoting: MysqlQuotingOptions = { ansiQuotes };
  // CTE language feature gate — MySQL 8.0+ / MariaDB 10.2+. Suppresses CTE
  // candidate emission below the threshold; the cteScope parser still runs
  // so branch isolation / correlated subquery behaviour is unaffected.
  const cteSupported = cteSupportedForServerVersion(native?.serverVersion);
  const mariadb = isMariaDb(native?.serverVersion);

  // Engine-extras scanners (PRD Slice "MySQL Resolver — Engine Extras").
  // All three are pure source-text walks; statement-scoped where possible.
  const newAlias = findInsertNewAlias(text, ctx.statementRange.start, ctx.statementRange.end);
  // F5.3-R-bucket: INSERT … (col_list) / INSERT … ON DUPLICATE KEY UPDATE
  // SET — surface the target table's columns when no row-alias is present.
  // The row-alias form (`INSERT … AS new ON DUPLICATE KEY UPDATE`) keeps
  // using `newAlias`; the legacy form has no alias to feed back, hence the
  // separate scan.
  const insertTarget = newAlias
    ? null
    : findInsertTarget(text, ctx.statementRange.start, ctx.statementRange.end);
  const inMatchContext = detectMatchAgainstContext(
    text,
    offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
  );
  // User vars are session-scoped — buffer-wide scan for `@var` references.
  const userVars = findUserVariables(text);
  // Detect whether the cursor is preceded by `@` or `@@` (looking back past
  // any partial identifier prefix). Drives gated emission of user/system vars.
  const atPrefix = detectAtVarPrefix(text, offset);

  // FULLTEXT column index — built from the native cache's per-table index
  // list. Used by MATCH AGAINST to restrict the column list. Empty map when
  // the native cache hasn't warmed yet.
  const fulltextColumnsByTable = buildFulltextIndex(native);

  // F5.2-CB2 — When the cursor is inside a CREATE TRIGGER body, NEW.|/OLD.|
  // bind to the trigger target table's columns.
  const triggerHostTable = findTriggerHostTable(text, offset, 'mysql');

  // F5.3 R-bucket — surface tables CREATEd earlier in the same buffer so
  // subsequent INSERT/SELECT statements see them and their columns even
  // before the schema-bus snapshot refreshes. Augments the snapshot table
  // list as a tier-1 prepend; the normal table-emit + alias-resolution paths
  // pick the new entries up transparently.
  const inBufferTables = findInBufferCreatedTables(text, offset);
  // Apply in-buffer RENAME/DROP to the snapshot FIRST, then merge in-buffer
  // CREATEs on top — so a `DROP TABLE foo; CREATE TABLE foo;` re-create leaves
  // `foo` present (applying ops after the merge would drop the just-created one).
  const augmentedTables = mergeInBufferTables(
    applyInBufferTableOps(snap?.tables ?? [], scanInBufferTableOps(text, offset, ENGINE), {
      keyOf: (t) => `${(t.database ?? '').toLowerCase()}.${t.name.toLowerCase()}`,
      keyOfRef: (q, n) => `${(q ?? '').toLowerCase()}.${n.toLowerCase()}`,
      renameTable: (existing, op: InBufferRename) => ({
        name: op.toName,
        database: op.toQualifier ?? effectiveDatabase ?? undefined,
        kind: existing?.kind ?? 'table',
        columns: existing?.columns ?? [],
      }),
    }),
    inBufferTables,
    effectiveDatabase,
  );

  // In-buffer CREATE VIEW — a view declared earlier in the buffer should be a
  // FROM target in later statements, with its columns resolving. A view's
  // columns are its SELECT projection (or an explicit `(col, …)` list), so
  // infer them from the captured body range.
  const tablesWithViews = mergeInBufferViews(
    augmentedTables,
    findInBufferCreatedViews(text, offset, ENGINE),
    text,
    ctx.cteBindings,
    effectiveDatabase,
  );

  const candidates = await buildCandidates({
    ctx,
    text,
    snapshotTables: tablesWithViews,
    snapshotSchemas: snap?.schemas ?? [],
    snapshotRoutines: snap?.routines ?? [],
    snapshotForeignKeys: snap?.foreignKeys ?? [],
    looseRels:
      input.connectionId && effectiveDatabase
        ? getLooseRelationships(input.connectionId, effectiveDatabase)
        : [],
    cteBindings: ctx.cteBindings,
    quoting,
    currentDatabase: effectiveDatabase,
    connectionId: input.connectionId,
    cteSupported,
    newAlias,
    insertTarget,
    inMatchContext,
    userVars,
    atPrefix,
    mariadb,
    fulltextColumnsByTable,
    triggerHostTable,
  });

  // `USING (▮)` / `ROLLUP(▮)` / Oracle's `SAMPLE (▮)` admit operands or
  // nothing — never the tables and FK-join snippets the full emission adds.
  const scoped = applyIndexTargetFilter(
    applyParenScopeFilter(candidates, text, offset, ctx.statementRange.start, ENGINE),
    text, offset, ctx.statementRange.start, ENGINE,
  );
  // GROUP BY scope (`context/groupByScope.ts`) — MySQL is the DEMOTE half of
  // the matrix: with ONLY_FULL_GROUP_BY off a bare non-grouped column is
  // accepted (and returns an arbitrary row's value), and the session's
  // sql_mode isn't visible from here, so the reference stays offered and is
  // only ranked below the grouped columns. Matches mysql script.53 §1's
  // "may be offered but should rank lower".
  const grouped = applyGroupByScopeFilter(scoped, {
    text,
    offset,
    statementStart: ctx.statementRange.start,
    statementEnd: ctx.statementRange.end,
    engineId: ENGINE,
    positionKind: ctx.positionKind,
  });
  const withDocs = enrichCandidatesWithDocs(grouped, {
    aliasBindings: ctx.aliasBindings,
    snapshotTables: tablesWithViews,
    foreignKeys: snap?.foreignKeys ?? [],
    fold: foldIdentifier,
  });
  const ranked = rankCandidates(withDocs, ctx, typed, rankOptions);

  const range = {
    startLineNumber: position.lineNumber,
    startColumn: wordInfo.startColumn,
    endLineNumber: position.lineNumber,
    endColumn: wordInfo.endColumn,
  };
  const suggestions = ranked.map((item) => toMonacoItem(item, monaco, range));
  return { suggestions };
}

// Merge in-buffer CREATE TABLE entries with the schema-bus snapshot. Skips
// in-buffer entries whose folded `<database>.<name>` collides with a
// snapshot entry — the snapshot already reflects the table.
function mergeInBufferTables(
  snapshot: ReadonlyArray<SnapshotTable>,
  inBuffer: ReadonlyArray<InBufferTable>,
  effectiveDatabase: string | undefined,
): ReadonlyArray<SnapshotTable> {
  if (inBuffer.length === 0) return snapshot;
  const keyOf = (db: string | null | undefined, name: string): string =>
    `${(db ?? '').toLowerCase()}.${name.toLowerCase()}`;
  const known = new Set<string>();
  for (const t of snapshot) known.add(keyOf(t.database, t.name));
  const merged: SnapshotTable[] = [...snapshot];
  for (const t of inBuffer) {
    const db = t.database ?? effectiveDatabase ?? null;
    if (known.has(keyOf(db, t.name))) continue;
    merged.push({
      name: t.name,
      database: db ?? undefined,
      kind: t.isTemporary ? 'temporary-table' : 'table',
      columns: t.columns.map((c) => ({ name: c.name, dataType: c.dataType })),
    });
  }
  return merged;
}

// Merge in-buffer CREATE VIEW entries with the snapshot. A view's columns are
// its explicit `(col, …)` list or its SELECT-body projection (inferred from the
// captured body range). Skips views whose `<database>.<name>` already exists in
// the snapshot. Returns `tables` unchanged when there are no in-buffer views.
function mergeInBufferViews(
  tables: ReadonlyArray<SnapshotTable>,
  views: ReadonlyArray<import('../context/inBufferTables').InBufferView>,
  text: string,
  cteBindings: readonly import('@/core/engines').CteBinding[],
  effectiveDatabase: string | undefined,
): ReadonlyArray<SnapshotTable> {
  if (views.length === 0) return tables;
  const keyOf = (db: string | null | undefined, name: string): string =>
    `${(db ?? '').toLowerCase()}.${name.toLowerCase()}`;
  const known = new Set<string>();
  for (const t of tables) known.add(keyOf(t.database, t.name));
  const merged: SnapshotTable[] = [...tables];
  for (const v of views) {
    const db = v.qualifier ?? effectiveDatabase ?? null;
    if (known.has(keyOf(db, v.name))) continue;
    // `inferSubqueryColumns` expects a paren-wrapped subquery body (its
    // outermost SELECT sits at paren-depth 1). A view's SELECT is bare, so
    // wrap it before inferring — keeps the shared helper untouched.
    const wrapped = `(${text.slice(v.bodyStart, v.bodyEnd)})`;
    const colNames =
      v.explicitColumns && v.explicitColumns.length > 0
        ? v.explicitColumns
        : inferSubqueryColumns({
            source: wrapped,
            bodyRange: { start: 0, end: wrapped.length },
            engineId: 'mysql',
            snapshotTables: merged,
            cteBindings,
          });
    if (colNames.length === 0) continue;
    known.add(keyOf(db, v.name));
    merged.push({
      name: v.name,
      database: db ?? undefined,
      // A CTAS (`CREATE TABLE t AS SELECT …`) travels through this same
      // projection-inference pass but materialises a TABLE.
      kind: v.relationKind ?? 'view',
      columns: colNames.map((name) => ({ name, dataType: '' })),
    });
  }
  return merged;
}

type SnapshotTable = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind: string;
  readonly columns: ReadonlyArray<{
    readonly name: string;
    readonly dataType: string;
    // Carried by the bus snapshot's ColumnMeta; read by the pkFirst mode of
    // emitMysqlTargetTableColumns (REFERENCES / constraint column-lists).
    readonly isPrimaryKey?: boolean;
    readonly jsonClassification?: import('@/features/sql-resolver/types/schema').JsonClassification;
    // Carried by the bus snapshot's ColumnMeta (T18b, D13) — read to build
    // the NULL keyword legality of a typed INSERT VALUES element.
    readonly nullable?: boolean;
  }>;
  // Indexes on the relation (`information_schema.STATISTICS`) — the
  // index-NAME vocabulary of `FORCE|USE|IGNORE INDEX (▮)` and
  // `[ALTER TABLE t] DROP INDEX ▮`.
  readonly indexes?: ReadonlyArray<{
    readonly name: string;
    readonly columns: readonly string[];
    readonly isUnique: boolean;
    readonly isPrimary: boolean;
  }>;
};

type SnapshotRoutine = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind: string;
  /** Return type (bus `RoutineMeta.returns`) — drives value-slot routine
   *  tiering (T10, mysql clone of T8); absent on objects without one. */
  readonly returns?: string;
};

type BuildArgs = {
  readonly ctx: ReturnType<typeof deriveCompletionContext>;
  readonly text: string;
  readonly snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly snapshotSchemas: readonly string[];
  readonly snapshotRoutines: readonly SnapshotRoutine[];
  readonly snapshotForeignKeys: readonly FkEdge[];
  readonly looseRels: readonly LooseRelationship[];
  readonly cteBindings: readonly CteBinding[];
  readonly quoting: MysqlQuotingOptions;
  readonly currentDatabase?: string;
  /** T18b — typed VALUES element enum-value lookup (MetaInfo is keyed per
   *  connection/database). Absent in some tests; the emitter degrades to no
   *  enum-value candidates when either this or `currentDatabase` is missing. */
  readonly connectionId?: string;
  readonly cteSupported: boolean;
  readonly newAlias: MysqlInsertNewAlias | null;
  readonly insertTarget: MysqlInsertTarget | null;
  readonly inMatchContext: boolean;
  readonly userVars: readonly string[];
  readonly atPrefix: 'user' | 'system' | null;
  readonly mariadb: boolean;
  readonly fulltextColumnsByTable: ReadonlyMap<string, ReadonlySet<string>>;
  readonly triggerHostTable: TriggerHostTable | null;
};

async function buildCandidates(args: BuildArgs): Promise<CompletionCandidate[]> {
  const {
    ctx,
    snapshotTables,
    snapshotSchemas,
    snapshotForeignKeys,
    quoting,
    currentDatabase,
  } = args;
  const out: CompletionCandidate[] = [];

  // Compound-keyword remainder — when the caret is attached to the second (or
  // third) word of a multi-word keyword (`CURRENT R▮`, `UNBOUNDED P▮`,
  // `NEXT V▮`, `REFRESH ON D▮`, `READS S▮`, `ON DUPLICATE K▮`), offer what is
  // left of that keyword. Runs FIRST so the keyword-only short-circuits below
  // (which return `out` as it stands) carry it; additive, so a caret whose head
  // word is really an identifier keeps everything else it had. See
  // `compoundKeywordRemainder.ts`.
  emitCompoundKeywordRemainders(
    out,
    ENGINE,
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.positionKind,
  );

  // MySQL stored-routine local-variable completion (ROUND2-C1.2).
  // When the cursor is inside a CREATE PROCEDURE/FUNCTION/TRIGGER … BEGIN … END
  // body, surface the DECLARE variables, parameters, and session user-variables
  // visible at the cursor (all at tier 1). Runs before the DML position-kind
  // branching so routine locals are merged with schema-based candidates.
  // The call is guarded inside `emitMysqlProceduralLocalCandidates` — it
  // degrades to an empty list when the MySQL ANTLR bundle is not yet loaded
  // or the parse fails, so DML candidates still flow.
  //
  // Slot gate: at non-expression slots (FROM table list, DDL column-list /
  // type / constraint slots) parameters and locals are not legal — surfacing
  // them there crowds out real schema names. Pinned by mysql script.42 §6 +
  // script.45 §6 where procedural locals leak into the top 5 at `FROM `.
  if (
    isInsideMysqlRoutineBody(args.text, ctx.offset) &&
    !isNonExpressionSlot(ctx.positionKind)
  ) {
    await emitMysqlProceduralLocalCandidates(args.text, ctx.offset, out);
  }

  const positionKind = ctx.positionKind;

  if (positionKind === 'dotted-access' && ctx.dottedBase) {
    emitDottedAccess(out, args);
    return out;
  }

  // `ROLLBACK TO ▮` / `RELEASE ▮` — a savepoint NAME slot. The name is
  // transaction state the buffer itself declared, so no schema emission can
  // reach it; before this the caret fell to `unknown` and answered with the
  // table list. Shared across all five engines (each spells the statements
  // differently — see `savepointSlot.ts`). Closes sqlite script.50 §9/§10.
  if (emitSavepointNameCandidates(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE)) {
    return out;
  }

  // `ALTER TABLE t RENAME TO ▮` — the NEW name. Every name the schema could
  // offer is a name the rename would collide with, so the slot is closed.
  // Shared with the other RENAME-capable engines (see `renameTargetSlot.ts`).
  if (isAtRenameTargetSlot(args.text, ctx.offset, ctx.statementRange.start, ENGINE)) {
    return out;
  }

  // A name the user is INVENTING (routine parameter, `DECLARE ▮` local /
  // cursor / handler, JSON_TABLE column) or a fixed literal (`PARTITIONS ▮`).
  // Closed slots: the module emits the small keyword set legal at each and
  // nothing else — see `declaredNameSlot.ts`. Closes mysql script.34 §1,
  // 41 §3, 45 §1, 46 §3/§5, 55 §1.
  if (emitDeclaredNameSlotCandidates(
    out, args.text, ctx.offset, ctx.statementRange.start, ENGINE,
  )) {
    return out;
  }

  // `DECLARE v_count ▮INT` — the procedural local's TYPE slot. The slot seam's
  // `isMysqlDataTypeSlot` covers the routine HEADER's parameter types only
  // (it requires the param-list paren), so a DECLARE inside a routine / event
  // body fell to `unknown`: the type names arrived only as tier-7 keywords and
  // the relation list came with them. Closes mysql script.39 §2.
  if (isAtDeclareTypeSlot(args.text, ctx.offset, ctx.statementRange.start, ENGINE)) {
    emitMysqlDataTypeCandidates(out);
    return out;
  }

  // Index-NAME slots. `FROM rental r FORCE INDEX (▮` is MySQL-ONLY SYNTAX
  // (see `indexNameSlot.ts`) and sits inside a `from` clause, so the clause
  // machine names the caret 'from' and the relation list answered it
  // (mysql script.22 §2). `DROP INDEX ▮ ON t` and `ALTER TABLE t DROP INDEX ▮`
  // fall to 'unknown' and got the same relation list. Both want the
  // relation's index names and nothing else.
  if (emitIndexNameCandidates(out, {
    text: args.text,
    offset: ctx.offset,
    statementStart: ctx.statementRange.start,
    statementEnd: ctx.statementRange.end,
    engine: ENGINE,
    tables: snapshotTables,
    format: formatIdentifier,
  })) {
    return out;
  }

  // Value-slot plan T10 (mysql clone of T8) — scalar-value branch. Reached
  // ONLY when the engine's capability table (or the T5b test setter) admits
  // the slot's role; LIVE in production as of the T13c flip
  // (`valueSlotCapabilities.ts`). The shared policy owns the candidate set;
  // MySQL built-in functions stay engine-emitted at their tiers, mirroring
  // the arm's own emission below.
  if (positionKind === 'scalar-value' && ctx.valueSlot) {
    const { adapter, scope } = buildMysqlValueSlotAdapter({
      slot: ctx.valueSlot,
      text: args.text,
      aliasBindings: ctx.aliasBindings,
      cteBindings: ctx.cteBindings,
      snapshotTables,
      snapshotRoutines: args.snapshotRoutines,
      snapshotForeignKeys,
      quoting,
      userVars: args.userVars,
      atPrefix: args.atPrefix,
      mariadb: args.mariadb,
      // C2 P1-1 — virtual system-table fallback: same key derivation as
      // `emitAliasColumns`'s F5.3 Wave-4 arm over MYSQL_SYSTEM_TABLE_COLUMNS
      // (database qualifier folds first; parser may park it in `schema`).
      virtualColumnsFor: (binding) => {
        const relationFolded = foldIdentifier(binding.relation, quoting);
        const dbRaw = binding.database ?? binding.schema;
        const dbFolded = dbRaw ? foldIdentifier(dbRaw, quoting) : null;
        const sysKey = dbFolded ? `${dbFolded}.${relationFolded}` : null;
        return sysKey
          ? MYSQL_SYSTEM_TABLE_COLUMNS[sysKey]?.map((name) => ({ name }))
          : undefined;
      },
    });
    await emitValueSlotCandidates(out, ctx.valueSlot, adapter, scope);
    emitBuiltinFunctions(out, args.snapshotRoutines);
    // T13c no-worse parity (F5.3-R, clone of pgsqlResolver's T13a /
    // mssqlResolver's T13b fix) — the clause path surfaces SELECT-list
    // projection aliases at HAVING positions (mirrors script.16's HAVING
    // alias pins on the other engines). A comparison-RHS slot that overrode
    // the HAVING clause must keep them. Recompute the clause with the same
    // machine `deriveCompletionContext` used (the slot path drops it);
    // WHERE/JOIN-ON slots never gain aliases — projections aren't computed
    // yet at those points in SQL evaluation order.
    const scalarClause = computeClauseContext(
      args.text, ctx.statementRange.start, ctx.offset, 'mysql',
    )?.clause;
    if (scalarClause === 'having') {
      emitSelectListAliasCandidates(
        out, args.text, ctx.statementRange, ctx.offset, ENGINE, 'having',
        (name) => (needsBacktickQuote(name) ? backtickQuote(name) : name),
      );
    }
    return out;
  }

  // Predicate-head plan P6b/P8b (mysql clone of the pgsql P5/P7 exemplar) —
  // predicate-head branch, LIVE in production since the P8b flip (mysql
  // carries all three head roles in `predicateHeadCapabilities.ts`). Reached
  // when the capability gate admits the head's role. Shape-mirrors the
  // scalar-value branch above: shared policy owns the candidate set, MySQL
  // built-in functions stay engine-emitted at their existing tier (the
  // PH-D4 builtin exemption — they carry no return metadata to tier
  // against, exactly as the scalar branch ships them).
  if (positionKind === 'predicate-head' && ctx.predicateHead) {
    const { adapter } = buildMysqlValueSlotAdapter({
      // No `slot`: PH-D3's no-LHS mode. The overload makes this a distinct
      // signature, so a scalar-value caller cannot reach it by dropping its
      // slot.
      text: args.text,
      aliasBindings: ctx.aliasBindings,
      cteBindings: ctx.cteBindings,
      snapshotTables,
      snapshotRoutines: args.snapshotRoutines,
      snapshotForeignKeys,
      quoting,
      userVars: args.userVars,
      atPrefix: args.atPrefix,
      mariadb: args.mariadb,
      virtualColumnsFor: (binding) => {
        const relationFolded = foldIdentifier(binding.relation, quoting);
        const dbRaw = binding.database ?? binding.schema;
        const dbFolded = dbRaw ? foldIdentifier(dbRaw, quoting) : null;
        const sysKey = dbFolded ? `${dbFolded}.${relationFolded}` : null;
        return sysKey
          ? MYSQL_SYSTEM_TABLE_COLUMNS[sysKey]?.map((name) => ({ name }))
          : undefined;
      },
    });
    await emitPredicateHeadCandidates(out, { head: ctx.predicateHead }, adapter);
    emitBuiltinFunctions(out, args.snapshotRoutines);
    // Post-policy parity (P1-sol F8) — the same engine-specific follow-up the
    // scalar-value branch replays: the clause path surfaces SELECT-list
    // projection aliases at HAVING positions, and a head slot that overrode
    // the HAVING clause must keep them. WHERE/JOIN-ON heads never gain
    // aliases — projections aren't computed yet at those points in SQL
    // evaluation order.
    const headClause = computeClauseContext(
      args.text, ctx.statementRange.start, ctx.offset, 'mysql',
    )?.clause;
    if (headClause === 'having') {
      emitSelectListAliasCandidates(
        out, args.text, ctx.statementRange, ctx.offset, ENGINE, 'having',
        (name) => (needsBacktickQuote(name) ? backtickQuote(name) : name),
      );
    }
    return out;
  }

  // §4.5 — named-window completion after `OVER `/`OVER (`. Additive (tier 1,
  // no short-circuit): the WINDOW-clause names rank above the position's
  // generic keyword/column vocabulary, which the pipeline still emits below.
  // Reads `QueryStructure.windows` for the current statement (decision 7).
  if (!ctx.insideStringOrComment) {
    emitNamedWindowCandidates({
      out,
      text: args.text,
      offset: ctx.offset,
      statementStart: ctx.statementRange.start,
      statementEnd: ctx.statementRange.end,
      engine: 'mysql',
    });
  }

  // GRA8-ROLL-4 — MySQL procedural-header data-type slot, routed by the
  // EditorContext slot seam (`isMysqlDataTypeSlot` → `data-type` CPK). The
  // CREATE PROCEDURE/FUNCTION parameter type slot emits the MySQL type
  // vocabulary and short-circuits so columns / tables / IN/OUT mode keywords do
  // not dilute (the anti-leak). Closes mysql script.33 §2.
  // `CAST(x AS ▮` / `CONVERT(x, ▮` sit in a NAMED clause (`select`), which the
  // slot seam is not allowed to override, so the type slot never reached this
  // branch and MySQL alone flooded there (DECIMAL at rank 51). A narrow surface
  // walk claims it, mirroring the pg / mssql / oracle text-walks.
  if (isMysqlCastTargetPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    emitMysqlCastTargetCandidates(out);
    return out;
  }

  if (positionKind === 'data-type') {
    emitMysqlDataTypeCandidates(out);
    return out;
  }

  // F5.3 Wave-4 — DDL slot dispatch (mirrors sqlite + pgsql resolvers).
  if (positionKind === 'ddl-index-column') {
    const target = findMysqlIndexTargetTable(args.text, ctx.statementRange.start, ctx.offset);
    if (target) emitMysqlTargetTableColumns(out, target, snapshotTables, currentDatabase);
    // Functional key parts (`CREATE INDEX ix ON t ((LOWER(name)))`, 8.0.13+)
    // make the built-in functions part of this slot's vocabulary — at their
    // usual tier 8, below the target table's columns. Cross-engine parity with
    // the sqlite / pgsql / oracle index-expression slots.
    emitBuiltinFunctions(out, args.snapshotRoutines);
    return out;
  }
  if (
    positionKind === 'ddl-create-object-name'
    // ENGINE DIVERGENCE — a MySQL INDEX name is never qualified (the index
    // lives in its table's schema), so the index-name slot keeps its existing
    // `IF NOT EXISTS` keyword emission further down instead of a database
    // list. Pinned by goals/autocomplete-coverage mysql script.31 §1/§6.
    && !isCreateIndexNameSlot(args.text.slice(ctx.statementRange.start, ctx.offset))
  ) {
    // `CREATE [OR REPLACE] VIEW|TABLE|PROCEDURE|… ▮` — the NEW object's name.
    // ENGINE DIVERGENCE (documented at the site per the parity rule): MySQL has
    // no schema layer — a "schema" IS a database — so the namespace vocabulary
    // here is the database list, not `snapshotSchemas`-as-schemas, and the
    // default is the session's current database rather than a fixed name like
    // `dbo` / `public`. Emission is otherwise identical to the other engines'.
    emitCreateObjectNamespaces(out, snapshotSchemas, {
      defaultName: currentDatabase,
      kind: 'database',
      detail: 'Database',
      format: formatIdentifier,
    });
    return out;
  }
  if (positionKind === 'ddl-fk-target') {
    // A FK can only reference a base table, never a view — exclude views so
    // they don't leak into the REFERENCES target (mysql script.29 §3 forbids
    // `customer_list` / `film_list`).
    const fkTargetTables = snapshotTables.filter((t) => t.kind !== 'view');
    emitTablesAndDatabases(out, fkTargetTables, snapshotSchemas, currentDatabase,
      ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, fkTargetTables)
          : new Set<string>(),
    );
    // Workstream C parity — `FOREIGN KEY (customer_id) REFERENCES ▮`: the FK
    // source-column stems name the likely target table; lift it above the
    // alphabetical listing (mirrors pgsql; mysql's §5 checkpoint is this bare
    // form in an ALTER TABLE).
    applyReferencesTargetPromotion(
      out,
      findFkSourceColumnStems(args.text, ctx.offset, ctx.statementRange.start),
    );
    return out;
  }
  if (positionKind === 'ddl-references-column-list') {
    const target = findMysqlReferencesTargetTable(args.text, ctx.statementRange.start, ctx.offset);
    if (target) {
      emitMysqlTargetTableColumns(out, target, snapshotTables, currentDatabase, {
        pkFirst: true,
      });
    }
    return out;
  }
  if (positionKind === 'ddl-alter-table-constraint-column-list') {
    const target = findMysqlAlterTableTarget(args.text, ctx.statementRange.start, ctx.offset);
    if (target) {
      emitMysqlTargetTableColumns(out, target, snapshotTables, currentDatabase, {
        pkFirst: true,
      });
    }
    // Merge in-progress ADD COLUMN decls so columns declared earlier in the
    // same ALTER TABLE statement surface at the constraint-clause slot.
    const addedColumns = parseMysqlInProgressAddedColumns(args.text, ctx.statementRange.start, ctx.offset);
    emitMysqlInProgressAddedColumns(out, addedColumns);
    return out;
  }
  // GRA8-ROLL-2 — CREATE TABLE in-progress-column slots routed by the
  // EditorContext slot seam (PARTITION BY <method>(▮ and a bare INDEX/KEY (▮).
  // The table does not exist in the snapshot yet, so emit the columns declared
  // so far in the CREATE TABLE preamble. Closes mysql script.41 §2 + 44 §3.
  if (positionKind === 'ddl-create-table-expression') {
    // Inside a `GENERATED ALWAYS AS (▮` / `AS (▮` generation expression the
    // other GENERATED columns are dropped: MySQL rejects a generated column
    // that references another one (and the column being defined cannot
    // reference itself). Closes mysql script.44 §2.
    const cols = parseMysqlInProgressCreateTableColumns(
      args.text,
      ctx.statementRange.start,
      ctx.offset,
      {
        excludeGenerated: isInsideGenerationExpression(
          args.text,
          ctx.statementRange.start,
          ctx.offset,
        ),
      },
    );
    emitMysqlInProgressAddedColumns(out, cols);
    return out;
  }

  // F5.3 Wave-4 — MySQL stored-routine characteristic-clause slot, between
  // the parameter-list `)` (or RETURNS type) and the BEGIN body. Emits the
  // 10 routine-characteristic keywords. Closes
  // goals/autocomplete-coverage mysql script.45 §3.
  if (isMysqlRoutineCharacteristicPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of MYSQL_ROUTINE_CHARACTERISTIC_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  // F5.3 Wave-4 — MySQL window-frame slot dispatch. Independent of
  // positionKind. Closes goals/autocomplete-coverage mysql script.54 §3/§4.
  if (isMysqlFrameStartPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of MYSQL_FRAME_START_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }
  if (isMysqlFrameEndPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of MYSQL_FRAME_END_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  // Statement-leading `CALL <ident>` references a stored procedure. The
  // generic position-kind classifier currently labels this slot as `'from'`
  // (or sometimes `'unknown'`) because `CALL <ident>` looks grammatically
  // like FROM <table> — but the user expects stored procedures, not tables.
  // Emit only user procedures (kind === 'procedure'); functions / tables /
  // views are explicitly suppressed. Closes goals/autocomplete-coverage
  // mysql script.37 §1.
  if (isAtCallStmtRoutineRef(args.text, ctx.statementRange.start, ctx.offset)) {
    const procs = args.snapshotRoutines.filter((r) => r.kind === 'procedure');
    emitRoutines(out, procs);
    return out;
  }

  // F5.3 Wave-4 — `CREATE [UNIQUE|FULLTEXT|SPATIAL] INDEX |` index-name
  // slot. MySQL 8.0.29+ accepts `IF NOT EXISTS` here. Closes
  // goals/autocomplete-coverage mysql script.31 §1.
  if (isMysqlCreateIndexNamePosition(args.text, ctx.offset, ctx.statementRange.start)) {
    out.push({
      label: 'IF NOT EXISTS',
      insertText: 'IF NOT EXISTS',
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'SQL keyword',
    });
    return out;
  }

  // I3 parity — INSERT target slot (`INSERT [LOW_PRIORITY|IGNORE|…] INTO ▮`).
  // The grammar lumps this under `values`, so the INSERT body's bound-alias
  // columns would otherwise bury the relation names. Fire only once INTO has
  // been typed (`!needsInto`) — the bare `INSERT ▮` / `INSERT ▮IGNORE` tail is
  // ambiguous with the INSERT modifier slot, so leave it to the keyword flow.
  const insertSlot = isAtInsertTargetSlot(args.text, ctx.offset, ctx.statementRange.start);
  if (insertSlot && !insertSlot.needsInto) {
    // INSERT targets may be updatable views, so views are kept here.
    emitTablesAndDatabases(out, snapshotTables, snapshotSchemas, currentDatabase,
      ctx.cteBindings.length === 0
        ? computeBufferReferencedTableKeys(args.text, snapshotTables)
        : new Set<string>(),
    );
    return out;
  }

  if (positionKind === 'from') {
    // Sub-position: cursor after a completed relation / alias (mirrors the
    // MSSQL resolver) — emit only continuation keywords, since another bare
    // relation here would be a syntax error.
    const fromSlot = classifyNextKeywordSlot(args.text, ctx.offset, ctx.statementRange.start, positionKind, ctx.aliasBindings, ENGINE);
    if (fromSlot && (fromSlot.slot === 'after-from-relation-plain' || fromSlot.slot === 'after-join-target')) {
      // Generated table-alias candidate leads the list (orderHint 0); the
      // continuation keywords shift to orderHint 1+. See aliasSuggestions.ts.
      const aliasEmitted = emitAliasCandidate(out, {
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        slot: fromSlot,
        aliasBindings: ctx.aliasBindings,
        keywords: MYSQL_KEYWORDS,
      });
      emitLegalNextKeywords(out, fromSlot, aliasEmitted ? 1 : 0);
      return out;
    }
    // Any OTHER slot the classifier names at a `from` position is a boundary
    // where a relation is illegal — a set operator starting a new query branch
    // (`… FROM t UNION ▮`), a shape keyword. Emit its keywords and stop, rather
    // than falling through to the table list below.
    //
    // A slot with an EMPTY list (the word is a construct in some other dialect,
    // e.g. a table legally named `tablesample` in MySQL) counts as DECLINED —
    // it must reach the after-relation keyword fallback below, not the bare
    // relation list, which is a syntax error at a completed relation.
    const fromSlotEmitted = !!fromSlot && emitLegalNextKeywords(out, fromSlot);
    if (fromSlotEmitted) return out;
    // Null-fallback: the classifier can decline an after-relation slot it can't
    // categorize confidently. When the broader `isAfterCompletedFromRelation`
    // text-walk still says we're past a completed relation, preserve the
    // pre-classify behavior rather than offering syntactically-illegal tables.
    if (!fromSlotEmitted && isAfterCompletedFromRelation(args.text, ctx.offset, ctx.statementRange.start)) {
      emitKeywords(out, MYSQL_NEXT_AFTER_FROM_RELATION);
      return out;
    }
    emitTablesAndDatabases(
      out,
      snapshotTables,
      snapshotSchemas,
      currentDatabase,
      unionTableKeys(
        computeFkLinkedTableKeys(
          ctx.aliasBindings,
          snapshotTables,
          snapshotForeignKeys,
          mysqlBindingResolver(snapshotTables, args.quoting),
        ),
        ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
      ),
    );
    // S6 — the relation already written at the caret's own binding outranks
    // the other tier-1 (FK-linked / buffer-referenced) tables.
    promoteCaretBindingRelation(out, ctx.aliasBindings, ctx.offset);
    if (args.cteSupported) emitCteCandidates(out, ctx);
    // F5.3 R-bucket — FK-driven JOIN snippets only form a syntactically
    // valid continuation at a JOIN target slot (`… JOIN |` / `… INNER
    // JOIN |` / `… LATERAL |`). At a bare-FROM-list slot (`FROM |x` or
    // `FROM a, |`) the snippet inserts an `ON …` clause that would be a
    // syntax error, and the snippets shove the plain table the user is
    // actually typing down the list. Demote to tier 7 there so they stay
    // discoverable via fuzzy match but don't outrank plain tables.
    if (ctx.aliasBindings.length > 0 && snapshotForeignKeys.length > 0) {
      const atJoinTargetSlot = isAtJoinTargetSlot(
        args.text,
        ctx.offset,
        ctx.statementRange.start,
      );
      emitFkJoinSnippets(
        out,
        ctx.aliasBindings,
        snapshotTables,
        snapshotForeignKeys,
        atJoinTargetSlot,
        args.quoting,
      );
    }
    if (ctx.aliasBindings.length > 0 && args.looseRels.length > 0) {
      emitLooseRelJoinSnippets({
        out,
        bindings: ctx.aliasBindings,
        tables: snapshotTables,
        looseRels: args.looseRels,
        realFks: snapshotForeignKeys,
        engine: ENGINE,
        resolveBinding: mysqlBindingResolver(snapshotTables, args.quoting),
      });
    }
    return out;
  }

  if (positionKind === 'ddl-trigger-event' || positionKind === 'ddl-create-trigger-timing') {
    // The slot seam already named these positions; only the emission was
    // missing, so the resolver flooded at a caret it knew exactly.
    const triggerSlot =
      positionKind === 'ddl-trigger-event' ? 'at-trigger-event' : 'at-trigger-timing';
    if (
      emitLegalNextKeywords(out, {
        slot: triggerSlot,
        relationHasAlias: false,
        projectionIsStar: false,
      })
    ) {
      return out;
    }
  }

  // `DELETE ▮r, p FROM rental r JOIN payment p …` — MySQL's multi-table
  // DELETE names its targets by the ALIASES the FROM clause introduces, so
  // this is the one slot where an alias reference is the whole answer. The
  // clause machine has no arm for it (there is no keyword between `DELETE` and
  // the list), so the caret fell to `unknown` and the generic emission
  // answered with columns and base-table names — both illegal here.
  // Closes mysql script.52 §1/§2.
  if (
    isMysqlMultiTableDeleteAliasSlot(args.text, ctx.offset, ctx.statementRange.start) &&
    ctx.aliasBindings.length > 0
  ) {
    for (const b of ctx.aliasBindings) {
      // Self-aliased relations (`FROM rental` → alias `rental`) are legal
      // targets too — MySQL accepts the table name where no alias was
      // spelled — so `explicitAlias` is not a filter here. Pseudo-relations
      // (NEW/OLD) are, since they are not FROM entries.
      if (b.relation.startsWith('__pseudo:')) continue;
      out.push({
        label: b.alias,
        insertText: b.aliasSql ?? b.alias,
        isSnippet: false,
        kind: 'alias',
        tier: 1,
        detail: `DELETE target — ${b.relation}`,
      });
    }
    return out;
  }

  if (
    positionKind === 'join-on' ||
    positionKind === 'where' ||
    positionKind === 'having' ||
    positionKind === 'set' ||
    positionKind === 'values' ||
    positionKind === 'returning' ||
    positionKind === 'group-by' ||
    positionKind === 'order-by' ||
    positionKind === 'select-list' ||
    positionKind === 'subquery' ||
    positionKind === 'cte-def' ||
    positionKind === 'limit-offset' ||
    positionKind === 'unknown'
  ) {
    // T18b — typed VALUES element (Docs/AutocompleteValueSlot-Plan.md). When
    // the cursor sits inside a VALUES element whose target column is
    // positively known (explicit column list; findInsertValuesTarget), the
    // resolved-target policy (T18a) replaces the generic values-position
    // emission below and short-circuits — columns/relations are structurally
    // illegal here. NO reclassification (G23): the position stays
    // positionKind 'values'; this is resolver-side dispatch only.
    if (positionKind === 'values') {
      const hit = emitTypedValuesElement(out, args, ctx);
      if (hit) return out;
    }
    // Position-aware continuation keywords via the shared legal-next-keyword
    // model. When the cursor sits after a completed predicate / list item at a
    // recognized expression sub-position, emit only the grammatically-legal
    // continuation keywords (popularity-ordered) and short-circuit. The FROM
    // sub-slots are handled by the dedicated `from` arm above, so they are
    // excluded here. A `null` classification (unrecognized sub-position) falls
    // through to today's column/table/keyword emission.
    const kwSlot = classifyNextKeywordSlot(args.text, ctx.offset, ctx.statementRange.start, positionKind, ctx.aliasBindings, ENGINE);
    // Anywhere inside the row-limiting clause (`LIMIT ▮` … `FETCH FIRST 10 ROWS
    // ONLY ▮`) only integers, the clause's own keywords and in-scope integer
    // operands are legal — relations, columns and the general keyword list are
    // a syntax error at every one of those carets. Emit that and stop; the
    // schema emission below must not run.
    if (kwSlot && isRowLimitSlot(kwSlot.slot)) {
      return buildRowLimitOperandCandidates(out, kwSlot.slot, ENGINE);
    }
    if (kwSlot && kwSlot.slot !== 'after-from-relation-plain' && kwSlot.slot !== 'after-join-target') {
      if (emitLegalNextKeywords(out, kwSlot)) return out;
    }
    if (ctx.aliasBindings.length > 0) {
      emitAllBoundAliasColumns(
        out,
        ctx.aliasBindings,
        snapshotTables,
        ctx.cteBindings,
        quoting,
        args.cteSupported,
        args.inMatchContext ? args.fulltextColumnsByTable : null,
        args.text,
      );
      // The trigger row qualifiers themselves (`NEW` / `OLD`). Inside a
      // trigger body there is no unqualified row scope, so one of these is the
      // first token the user must type to reach a column — yet only their
      // COLUMNS were ever emitted, never the qualifier.
      emitTriggerPseudoRelationNames(
        out, args.text, ctx.statementRange, ctx.aliasBindings, ENGINE, ctx.offset,
      );
    }
    // INSERT … AS new ON DUPLICATE KEY UPDATE — surface the alias's columns
    // unqualified (the user can write `col = …` or `alias.col = …`).
    if (args.newAlias) {
      emitInsertNewAliasColumns(out, args.newAlias, snapshotTables, quoting);
    }
    // F5.3-R-bucket: legacy INSERT INTO target (col_list) and the column-
    // assignment LHS of `ON DUPLICATE KEY UPDATE` (no row-alias form) —
    // surface the target table's columns at tier 1. Only fires at the
    // `values` / `set` positions so unrelated bare positions are unaffected.
    if (
      args.insertTarget &&
      (positionKind === 'values' || positionKind === 'set')
    ) {
      emitInsertTargetColumns(out, args.insertTarget, snapshotTables, quoting);
    }
    // T16: the S0 value-slot relation guard (plan: Docs/AutocompleteValueSlot-Plan.md
    // T3/T4) that used to gate the two emitters below on
    // `!detectComparisonValueContext(...)` is RETIRED — every comparison-value
    // operand in where/having/join-on now classifies via the LIVE scalar-value
    // branch above (comparison-rhs, set-rhs, between-low, between-high), which
    // returns before this expression arm is ever reached.
    //
    // P8b (Docs/AutocompletePredicateHead-Plan.md): bare predicate HEADS
    // (`WHERE ▮`, `HAVING ▮`, `ON ▮`, `AND/OR/NOT ▮` continuations) no longer
    // reach this arm either — they classify 'predicate-head' and return from
    // the LIVE head branch above, where relations are FORBIDDEN_KINDS
    // (`WHERE <table>` is illegal SQL). What still lands here under
    // where/having/join-on is the recognizer's conservative refusals —
    // mid-expression boundaries (`WHERE x ▮`), `WHERE (▮` open-paren heads,
    // CASE WHEN heads, IS-chain territory (PH-D6 v1 gaps) — plus the
    // non-predicate positionKinds of this arm (select-list/unknown/…). For
    // those the full relation emission below is unchanged by design (the
    // no-worse invariant; the v1 gaps are recorded in the P10 gaps sweep).
    emitTablesAndDatabases(out, snapshotTables, snapshotSchemas, currentDatabase,
      ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
    );
    if (args.cteSupported) emitCteCandidates(out, ctx);
    // In an expression position only stored FUNCTIONS are invokable; stored
    // PROCEDURES are reachable only via `CALL` (handled by the dedicated
    // isAtCallStmtRoutineRef slot above), so suppress them here.
    emitRoutines(out, args.snapshotRoutines.filter((r) => r.kind !== 'procedure'));
    emitBuiltinFunctions(out, args.snapshotRoutines);
    // F5.3-R-bucket: surface SELECT-list projection aliases in HAVING /
    // ORDER-BY / GROUP-BY positions. MySQL accepts an unqualified `AS`-bound
    // alias as a reference in all three clauses.
    emitSelectListAliasCandidates(
      out,
      args.text,
      ctx.statementRange,
      ctx.offset,
      ENGINE,
      positionKind,
      (name) => (needsBacktickQuote(name) ? backtickQuote(name) : name),
    );
    // F5.2-CB1.7 — when the cursor sits AFTER a completed projection in the
    // SELECT list (`SELECT * `, `SELECT col1, col2 `), promote FROM / set-op
    // continuation keywords to tier 1. Other positions (where, join-on,
    // having, group-by, order-by, set, values, returning, cte-def, …) fall
    // through with the default empty set → keywords stay at tier 7.
    const nextLikely =
      positionKind === 'select-list' &&
      isAfterCompletedSelectProjection(
            args.text, ctx.offset, ctx.statementRange.start, scanOptionsFor(ENGINE),
          )
        ? MYSQL_NEXT_AFTER_SELECT_PROJECTION
        : (new Set<string>() as ReadonlySet<string>);
    // At a routine/trigger body statement-start (`BEGIN … ; |`) the user's next
    // token is almost always a keyword (SELECT/IF/SET/DECLARE/…) or a declared
    // local, not a schema object — promote the whole keyword list above tables
    // (tier 2) so it stays in Monaco's virtualized viewport; declared locals at
    // tier 1 remain on top.
    const procBodyStart =
      isInsideRoutineBodyBlock(args.text, ctx.offset, ENGINE) &&
      isAtProceduralStatementStart(args.text, ctx.offset, ENGINE);
    emitProjectionWildcard(out, positionKind, ENGINE);
    emitKeywords(out, nextLikely, procBodyStart ? 2 : 7);
    // F5.3 Wave-4 — two-token operators at WHERE/HAVING. `IS NULL` /
    // `IS NOT NULL` are null-comparison operators; `NOT EXISTS` is the
    // negated-subquery-existence operator. None are in the curated
    // single-keyword list, so push them directly as tier-1 candidates.
    if (positionKind === 'where' || positionKind === 'having') {
      for (const kw of MYSQL_WHERE_NULL_OPERATOR_KEYWORDS) {
        out.push({
          label: kw,
          insertText: kw,
          isSnippet: false,
          kind: 'keyword',
          tier: 1,
          detail: 'SQL keyword',
        });
      }
    }
    // JCS Slice C3: operator suggestion is gated on the cursor's preceding
    // operand. MySQL JSON ops (`->`, `->>`, `JSON_CONTAINS`, `MEMBER OF`, etc.)
    // only surface when the LHS column is JSON-classified.
    emitAggregateArgHints(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE);
    emitAggregateTailHints(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE);
    emitTypeAwareJsonOperators(out, args);
    // User variables declared in this buffer (`SET @x := …`) are session-scoped
    // state that should surface in any expression position — not only when the
    // user has already typed `@`. Monaco's @-aware filter handles both
    // `@rn|`-style typing (filtered by suffix) and bare typing (label match).
    // System variables stay gated behind `@@` because their full list is large.
    if (args.userVars.length > 0) emitUserVariables(out, args.userVars);
    if (args.atPrefix === 'system') emitSystemVariables(out, args.mariadb);
    // F5.2-A — drop already-typed columns from the next slot inside an
    // INSERT column-list (`INSERT INTO t (a, |)` must not re-offer `a`).
    // A column may be assigned only once in an UPDATE, so the SET list's
    // already-assigned names are illegal at the next slot.
    filterAlreadyAssignedSetColumns(
      out, args.text, ctx.offset, ctx.statementRange.start, ctx.statementRange.end, ENGINE,
    );
    if (positionKind === 'values') {
      filterAlreadyTypedInsertColumns(out, args, ctx);
    }
    return out;
  }

  emitTablesAndDatabases(out, snapshotTables, snapshotSchemas, currentDatabase,
    ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
  );
  emitKeywords(out);
  return out;
}

/**
 * T18b — when the cursor sits inside a VALUES element whose target column is
 * positively known (`findInsertValuesTarget`), resolve the target column in
 * the snapshot (`resolveRelation` — the same database-scoped lookup the
 * enum/JSON-path phase-3 collectors use) and emit the shared resolved-target
 * policy (T18a) — enum/SET values (tier 1, native inline members as
 * fallback), NULL/DEFAULT keywords (D13), a scalar-subquery snippet, and
 * compat-tiered scalar routines. Returns `false` (caller falls through to
 * the unchanged generic emission) when the element isn't positively typed or
 * the target table/column can't be resolved.
 */
function emitTypedValuesElement(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): boolean {
  // Trigger bodies put a row scope in play (`NEW.`/`OLD.`), so columns are
  // NOT structurally illegal at VALUES elements there — the resolved-target
  // premise (`legalSources.columns: false`) fails and the generic emission
  // must serve the position. Pinned by sqlite coverage script.36 §5/§6.
  if (args.triggerHostTable) return false;
  const hit = findInsertValuesTarget(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!hit) return false;
  const tbl = resolveRelation(
    args.snapshotTables,
    foldIdentifier(hit.table.name, args.quoting),
    hit.table.schema ? foldIdentifier(hit.table.schema, args.quoting) : null,
  );
  if (!tbl) return false;
  const colFolded = foldIdentifier(hit.columnName, args.quoting);
  const column = tbl.columns.find((c) => foldIdentifier(c.name, args.quoting) === colFolded);
  if (!column) return false;

  const columnMeta: ColumnMeta = {
    name: column.name,
    dataType: column.dataType,
    nullable: column.nullable ?? false,
    isPrimaryKey: column.isPrimaryKey ?? false,
    ordinal: 0,
  };

  const connectionId = args.connectionId;
  const database = args.currentDatabase;
  const target: ResolvedValueTarget = {
    kind: 'insert-column',
    column: columnMeta,
    coarseType: coarseTypeOfDataType(column.dataType),
    legalSources: { columns: false, keywords: legalValueKeywordsFor(ENGINE, columnMeta) },
    collectEnumValues:
      connectionId && database
        ? () => {
            const tmp: CompletionCandidate[] = [];
            emitEnumValueCandidates(tmp, {
              connectionId,
              database,
              schema: tbl.schema ?? tbl.database ?? '',
              table: tbl.name,
              column: column.name,
              quote: findValueStringBodyStart(args.text, ctx.offset) === undefined,
              tier: 1,
              nativeFallbackMembers: parseInlineEnumMembers(column.dataType) ?? undefined,
            });
            return tmp;
          }
        : undefined,
  };

  emitResolvedValueCandidates(out, target, {
    engine: ENGINE,
    collectScalarRoutines: () => collectMysqlScalarRoutineDescriptors(args.snapshotRoutines),
  });
  // Builtin scalar functions (NOW(), UUID(), …) are legal VALUES elements —
  // same builtins-after-policy shape as the scalar-value branch (C3 sol
  // finding 2). Shadow-filtered against user routines, which the policy
  // already emitted above.
  emitBuiltinFunctions(out, args.snapshotRoutines);
  return true;
}

/** F5.2-A — drop column candidates whose label matches a column already
 *  typed earlier in the same INSERT column-list. No-op outside INSERT
 *  column-list (the helper returns null then). */
function filterAlreadyTypedInsertColumns(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): void {
  const typed = findTypedInsertColumns(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!typed || typed.length === 0) return;
  const typedSet = new Set(typed);
  for (let i = out.length - 1; i >= 0; i -= 1) {
    const c = out[i];
    if (c.kind !== 'column') continue;
    if (typedSet.has(c.label.toLowerCase())) {
      out.splice(i, 1);
    }
  }
}

function emitDottedAccess(out: CompletionCandidate[], args: BuildArgs): void {
  const { ctx, snapshotTables, cteBindings, quoting, triggerHostTable } = args;
  if (!ctx.dottedBase) return;
  const { parts } = ctx.dottedBase;
  if (parts.length === 0) return;
  const baseFolded = foldIdentifier(parts[0]!, quoting);

  if (parts.length === 1) {
    // INSERT … AS new ON DUPLICATE KEY UPDATE — `new.<col>` resolves to the
    // INSERT target's columns. Checked before the alias-binding sweep so an
    // unrelated alias named `new` doesn't shadow it (the alias scope walker
    // has no signal that `new` is a row-alias special form).
    if (args.newAlias && foldIdentifier(args.newAlias.alias, quoting) === baseFolded) {
      emitInsertNewAliasColumns(out, args.newAlias, snapshotTables, quoting);
      return;
    }
    // F5.2-CB2 — `NEW.col` / `OLD.col` inside a CREATE TRIGGER body resolves
    // to the trigger target-table's columns. Checked before alias-binding
    // since aliasScope/pseudoTables emits a pseudo-binding with no column
    // list — falling through there would emit nothing.
    if ((baseFolded === 'new' || baseFolded === 'old') && triggerHostTable) {
      emitMysqlTriggerHostColumns(out, triggerHostTable, snapshotTables, baseFolded, quoting);
      return;
    }
    const aliasMatch = ctx.aliasBindings.find(
      (b) => foldIdentifier(b.alias, quoting) === baseFolded,
    );
    if (aliasMatch) {
      emitAliasColumns(out, aliasMatch, snapshotTables, cteBindings, quoting, args.cteSupported, null, args.text);
      // F5.2-CB5 + S3 — FK/partner-aware column ranking at JOIN ON / WHERE
      // dotted positions (partner affinity → endpoint-pair FKs → name∩+PK).
      applyJoinOnColumnRanking({
        out,
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        sqlEngine: 'mysql',
        bindings: ctx.aliasBindings,
        edges: args.snapshotForeignKeys,
        binding: aliasMatch,
        tables: snapshotTables,
      });
      // I2 parity — `JSON_EXTRACT(p.▮` lifts the JSON document column:
      // buffer-affinity ∪ bus-classified JSON columns (mysql fn-set).
      if (
        isAtJsonFunctionFirstArg(
          args.text, ctx.offset, ctx.statementRange.start, MYSQL_JSON_FN_NAMES,
        )
      ) {
        applyJsonFunctionArgPromotion(
          out, collectMysqlKnownJsonColumns(args, aliasMatch),
        );
      }
      return;
    }
    if (args.cteSupported) {
      const cteMatch = ctx.cteBindings.find(
        (c) => foldIdentifier(c.name, quoting) === baseFolded,
      );
      if (cteMatch) {
        emitCteColumnList(out, cteMatch, {
          source: args.text,
          engineId: 'mysql',
          snapshotTables,
          cteBindings,
        });
        // S3 partner affinity for CTE projections (`ON child.manager_id = ec.▮`
        // wants the FK pair `employee_id`) — name-based, no FK metadata on
        // the CTE itself is needed.
        applyJoinOnColumnRanking({
          out,
          text: args.text,
          offset: ctx.offset,
          statementStart: ctx.statementRange.start,
          sqlEngine: 'mysql',
          bindings: ctx.aliasBindings,
          edges: args.snapshotForeignKeys,
        });
        return;
      }
    }
    // database.<table> — emit tables in the named database. The bus snapshot
    // only carries the current database's tables today, so this branch
    // surfaces tables only when the user dotted into the current DB by name
    // (e.g. `SELECT * FROM sakila.<here>` while connected to `sakila`).
    emitTablesInDatabase(out, snapshotTables, baseFolded, quoting);
    // F5.3 Wave-4 — fallback: when the schema is a known system database
    // (information_schema, mysql, performance_schema, sys), surface its
    // documented table list. Closes goals/autocomplete-coverage mysql
    // script.19 §3 (information_schema.|) and script.20 §3 (mysql.|).
    const sysTables = MYSQL_SYSTEM_SCHEMA_TABLES[baseFolded];
    if (sysTables) {
      for (const t of sysTables) {
        out.push({
          label: t,
          insertText: formatIdentifier(t),
          isSnippet: false,
          kind: 'table',
          tier: 2,
          detail: `${baseFolded} system table`,
        });
      }
    }
    return;
  }

  // Two-part `database.table.<col>` — match snapshot tables on (database, name).
  if (parts.length >= 2) {
    const part0Folded = foldIdentifier(parts[0]!, quoting);
    const part1Folded = foldIdentifier(parts[1]!, quoting);
    const tbl = snapshotTables.find(
      (t) =>
        (t.database ?? '').toLowerCase() === part0Folded &&
        t.name.toLowerCase() === part1Folded,
    );
    if (tbl) {
      for (const c of tbl.columns) {
        out.push({
          label: c.name,
          insertText: formatIdentifier(c.name),
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `${c.dataType} (${tbl.database ?? ''}.${tbl.name})`,
        });
      }
    }
  }
}

function emitTablesInDatabase(
  out: CompletionCandidate[],
  tables: ReadonlyArray<SnapshotTable>,
  databaseFolded: string,
  quoting: MysqlQuotingOptions,
): void {
  for (const t of tables) {
    if ((t.database ?? '').toLowerCase() !== databaseFolded) continue;
    out.push({
      label: formatIdentifier(t.name),
      insertText: formatIdentifier(t.name),
      isSnippet: false,
      kind: t.kind === 'view' ? 'view' : 'table',
      tier: 2,
      detail: t.kind === 'view' ? 'View' : 'Table',
      filterText: t.name,
    });
  }
  void quoting;
}

function makeTableCandidate(
  t: SnapshotTable,
  currentDatabase: string | undefined,
  fkLinked = false,
): CompletionCandidate {
  const db = t.database ?? '';
  const sameDb =
    currentDatabase && db.toLowerCase() === currentDatabase.toLowerCase();
  // Tables in the connection's current database get bare insertText (the
  // common case — users write `FROM film`, not `FROM sakila.film`). Tables
  // from a different database (cross-database queries — currently not
  // populated by the bus snapshot, but kept for forward compatibility)
  // qualify with `db.name`.
  const insertText = sameDb || !db
    ? formatIdentifier(t.name)
    : formatQualifiedTwoPart(db, t.name);
  // Label always shows just the table name when in current DB; otherwise
  // include the database prefix so the user can disambiguate.
  const label = sameDb || !db ? t.name : `${db}.${t.name}`;
  // Tier-8 demotion for tables in MySQL system databases (information_schema,
  // mysql, performance_schema, sys). Power users still find them via fuzzy
  // match; regular completion lists user tables first.
  // FK-linked tables (an FK edge connects them to a bound relation) promote
  // to tier 1 — the actually-joinable candidates at a JOIN slot.
  const isSystem = isMysqlSystemDatabase(t.database);
  return {
    label,
    insertText,
    isSnippet: false,
    kind: t.kind === 'view' ? 'view' : 'table',
    tier: isSystem ? 8 : fkLinked ? 1 : 2,
    detail: isSystem
      ? `System ${t.kind === 'view' ? 'View' : 'Table'}`
      : t.kind === 'view' ? 'View' : 'Table',
    filterText: t.name,
  };
}

function emitTablesAndDatabases(
  out: CompletionCandidate[],
  tables: ReadonlyArray<SnapshotTable>,
  databases: readonly string[],
  currentDatabase: string | undefined,
  fkLinkedKeys?: ReadonlySet<string>,
): void {
  // Other databases at tier 4 (other-visible-schemas slot). We surface the
  // current database here too because users sometimes prefer the explicit
  // form `currentdb.table`. The list comes from the snapshot's `schemas`
  // field, which the MySQL adapter populates from `SHOW DATABASES`.
  const seenLower = new Set<string>();
  for (const s of databases) {
    seenLower.add(s.toLowerCase());
    out.push({
      label: formatIdentifier(s),
      insertText: formatIdentifier(s),
      isSnippet: false,
      kind: 'database',
      tier: 4,
      detail: 'Database',
      filterText: s,
    });
  }
  // F5.3-R-bucket: system databases (information_schema, mysql,
  // performance_schema, sys) at tier 8 — visible for three-part references
  // (`information_schema.tables.…`) but ranked below user databases. The
  // introspection layer filters them out of the regular list to avoid tier-4
  // pollution, so the resolver re-emits them here. Skip any that are already
  // present in `databases` (defensive — most installs filter them upstream).
  for (const s of MYSQL_SYSTEM_DATABASES) {
    if (seenLower.has(s)) continue;
    out.push({
      label: formatIdentifier(s),
      insertText: formatIdentifier(s),
      isSnippet: false,
      kind: 'database',
      tier: 8,
      detail: 'System database',
      filterText: s,
      // Engine-shipped curated set (4 names) sharing the unbounded 'database'
      // kind at tier 8 (deepest — cut FIRST by the untyped cap) — flag so
      // they survive on >500-candidate pools (Plan 15 Gate D round 2 class
      // kill; the oracle twin was script.24 §2's data-dictionary synonyms).
      capExempt: true,
    });
  }
  // Tables at tier 2 (current-database tables); FK-linked ones at tier 1.
  for (const t of tables) {
    out.push(
      makeTableCandidate(t, currentDatabase, fkLinkedKeys?.has(fkLinkedTableKey(t)) ?? false),
    );
  }
}

function emitCteCandidates(out: CompletionCandidate[], ctx: BuildArgs['ctx']): void {
  for (const cte of ctx.cteBindings) {
    out.push({
      label: cte.name,
      insertText: cte.name,
      isSnippet: false,
      kind: 'cte',
      tier: 5,
      detail: 'CTE',
      filterText: cte.name,
    });
  }
}

function emitRoutines(
  out: CompletionCandidate[],
  routines: readonly SnapshotRoutine[],
): void {
  for (const r of routines) {
    out.push({
      label: r.name,
      insertText: `${r.name}($0)`,
      isSnippet: true,
      kind: 'routine',
      tier: 3,
      detail: r.kind ?? 'routine',
      filterText: r.name,
      triggerSignatureHelp: true,
    });
  }
}

/**
 * Emit MySQL built-in function names from the curated list. Deduped against
 * user-routine candidates by lower-cased name so a user-defined function
 * that shadows a built-in surfaces only once. Tier 8 (system-function) so
 * built-ins sit at the bottom of the suggestion list — power users still
 * find them via fuzzy match.
 */
function emitBuiltinFunctions(
  out: CompletionCandidate[],
  userRoutines: readonly SnapshotRoutine[],
): void {
  const userNames = new Set<string>();
  for (const r of userRoutines) userNames.add(r.name.toLowerCase());
  for (const fn of getMysqlBuiltinFunctions()) {
    if (userNames.has(fn.toLowerCase())) continue;
    out.push({
      label: fn,
      insertText: `${fn}($0)`,
      isSnippet: true,
      kind: 'system-function',
      tier: 8,
      detail: 'MySQL function',
      filterText: fn,
      triggerSignatureHelp: true,
    });
  }
}

/**
 * Position-aware continuation keywords via the shared legal-next-keyword model.
 * Emits the grammatically-legal continuation keywords for the classified slot
 * at tier 1, carrying the model's popularity order through `orderHint` (index 0
 * = dominant continuation). Returns `true` when at least one keyword was
 * emitted, so the caller can short-circuit the full-list fallback.
 */
function emitLegalNextKeywords(
  out: CompletionCandidate[],
  slot: NextKeywordSlotResult,
  // Shifts the popularity order when a leading candidate (the generated
  // table alias) already occupies orderHint 0.
  orderHintBase = 0,
): boolean {
  const kws = getLegalNextKeywords(ENGINE, slot.slot, {
    relationHasAlias: slot.relationHasAlias,
    projectionIsStar: slot.projectionIsStar,
  });
  if (kws.length === 0) return false;
  // The slot IS answered whenever it has a vocabulary, so `true` (the caller's
  // short-circuit) does not depend on how many items are new — a keyword an
  // earlier, more specific emission already offered (a compound remainder at
  // `NULLS F▮`) is skipped rather than repeated in the widget.
  const alreadyOffered = keywordLabelsAlreadyOffered(out);
  kws.forEach((kw, i) => {
    if (alreadyOffered.has(kw.toUpperCase())) return;
    out.push({ label: kw, insertText: kw, isSnippet: false, kind: 'keyword', tier: 1, detail: 'SQL keyword', orderHint: orderHintBase + i });
  });
  return true;
}

function emitKeywords(
  out: CompletionCandidate[],
  nextLikely: ReadonlySet<string> = new Set(),
  // Base tier for keywords not in `nextLikely`. Defaults to 7 (below schema
  // objects). At a procedural body statement-start the caller passes 2 so the
  // full keyword list ranks above tables/columns (a keyword is the most likely
  // next token there) while declared locals at tier 1 stay on top.
  baseTier: CompletionCandidateTier = 7,
): void {
  const alreadyOffered = keywordLabelsAlreadyOffered(out);
  for (const kw of getMysqlKeywordsForPosition()) {
    // A more specific emission already offered this keyword (e.g. a compound
    // remainder at tier 1) — don't repeat it in the widget.
    if (alreadyOffered.has(kw)) continue;
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: nextLikely.has(kw) ? 1 : baseTier,
      detail: 'SQL keyword',
    });
  }
}

/**
 * F5.3 Wave-4 — extract the target table from the `ON [<db>.]<tbl> (` segment
 * of a `CREATE INDEX ... ON <tbl> (` MySQL statement. Returns `{ database,
 * name }` — the database folded, the table name as WRITTEN (unquoted): with
 * `lower_case_table_names = 0` a table name is case-sensitive whether
 * backticked or not, so `Orders` and `orders` are two tables and the spelling
 * decides (`emitMysqlTargetTableColumns`). Null when the segment isn't
 * recognisable. Same for the REFERENCES / ALTER TABLE readers below.
 */
function findMysqlIndexTargetTable(
  text: string,
  statementStart: number,
  cursorOffset: number,
): { database: string | null; name: string } | null {
  const slice = text.slice(statementStart, cursorOffset);
  const m = slice.match(
    /\bON\s+(?:(`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s*\.\s*)?(`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s*\(/i,
  );
  if (!m || !m[2]) return null;
  const dbRaw = m[1] ?? null;
  const nameRaw = m[2]!;
  return {
    database: dbRaw ? foldIdentifier(unquoteIdentifier(dbRaw)) : null,
    name: unquoteIdentifier(nameRaw),
  };
}

/**
 * F5.3 Wave-4 — extract the REFERENCED table from the LAST
 * `REFERENCES [<db>.]<tbl>(` segment in the slice (multi-FK statements pick
 * the last match). Returns folded `{ database, name }` or null.
 */
function findMysqlReferencesTargetTable(
  text: string,
  statementStart: number,
  cursorOffset: number,
): { database: string | null; name: string } | null {
  const slice = text.slice(statementStart, cursorOffset);
  const re = /\bREFERENCES\s+(?:(`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s*\.\s*)?(`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s*\(/gi;
  let m: RegExpExecArray | null = null;
  let last: RegExpExecArray | null = null;
  while ((m = re.exec(slice)) !== null) last = m;
  if (!last || !last[2]) return null;
  const dbRaw = last[1] ?? null;
  const nameRaw = last[2]!;
  return {
    database: dbRaw ? foldIdentifier(unquoteIdentifier(dbRaw)) : null,
    name: unquoteIdentifier(nameRaw),
  };
}

/**
 * F5.3 Wave-4 — extract the ALTER TABLE target from `ALTER TABLE [<db>.]<tbl>`
 * in the cursor's statement preamble. Used by the
 * `ddl-alter-table-constraint-column-list` slot to surface the altered
 * table's columns at PRIMARY KEY / UNIQUE / FOREIGN KEY source slots.
 */
function findMysqlAlterTableTarget(
  text: string,
  statementStart: number,
  cursorOffset: number,
): { database: string | null; name: string } | null {
  const slice = text.slice(statementStart, cursorOffset);
  const m = slice.match(
    // Trailing `(?![\w$])` (not `\b`): a closing backtick is a non-word char,
    // so `\b` never matched after a quoted target followed by whitespace/EOL.
    /\bALTER\s+TABLE\s+(?:(`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s*\.\s*)?(`(?:[^`]|``)*`|[A-Za-z_][\w$]*)(?![\w$])/i,
  );
  if (!m || !m[2]) return null;
  const dbRaw = m[1] ?? null;
  const nameRaw = m[2]!;
  return {
    database: dbRaw ? foldIdentifier(unquoteIdentifier(dbRaw)) : null,
    name: unquoteIdentifier(nameRaw),
  };
}

/**
 * F5.3 Wave-4 — scan `ALTER TABLE … ADD [COLUMN] <name> <type> [, …]` clauses
 * in the cursor's statement preamble, returning the folded names of columns
 * already declared (but not yet committed) earlier in the same statement.
 * Used by the `ddl-alter-table-constraint-column-list` slot so columns added
 * earlier in the multi-action ALTER also surface at the FK/PK/UNIQUE slot.
 */
function parseMysqlInProgressAddedColumns(
  text: string,
  statementStart: number,
  cursorOffset: number,
): string[] {
  const slice = text.slice(statementStart, cursorOffset);
  // Strip everything up to and including the first ALTER TABLE keyword, so
  // the regex only scans the action-list region.
  const altMatch = slice.match(/\bALTER\s+TABLE\b/i);
  if (!altMatch) return [];
  const actionList = slice.slice(altMatch.index! + altMatch[0].length);
  const re = /\bADD\s+(?:COLUMN\s+)?(`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s+(?!CONSTRAINT\b|FOREIGN\s+KEY\b|PRIMARY\s+KEY\b|UNIQUE\b|INDEX\b|KEY\b|FULLTEXT\b|SPATIAL\b)[A-Za-z]/gi;
  const out: string[] = [];
  let m: RegExpExecArray | null = null;
  while ((m = re.exec(actionList)) !== null) {
    if (m[1]) out.push(foldIdentifier(unquoteIdentifier(m[1])));
  }
  return out;
}

const MYSQL_CREATE_TABLE_HEADER_RE =
  /\bCREATE\s+(?:TEMPORARY\s+)?TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?(?:`(?:[^`]|``)*`|"(?:[^"]|"")*"|[A-Za-z_][\w$]*(?:\s*\.\s*(?:`(?:[^`]|``)*`|"(?:[^"]|"")*"|[A-Za-z_][\w$]*))?)\s*\(/i;

const MYSQL_FIRST_IDENT_RE =
  /^\s*(`(?:[^`]|``)*`|"(?:[^"]|"")*"|[A-Za-z_][\w$]*)/;

// Segment-lead keywords that introduce a table-constraint / index clause rather
// than a column definition — skip them (same exclusion set the in-progress
// ADD-COLUMN regex uses). Matched case-insensitively against the segment's
// first bare word.
const MYSQL_TABLE_CONSTRAINT_LEAD: ReadonlySet<string> = new Set([
  'constraint', 'primary', 'foreign', 'unique', 'check', 'index', 'key',
  'fulltext', 'spatial',
]);

/**
 * GRA8-ROLL-2 — scan the `CREATE TABLE … (` column-definition preamble up to
 * the cursor and return the leading bare identifier of each top-level (depth-1)
 * column definition. Used by the `ddl-create-table-expression` slot so a
 * PARTITION BY expression or a bare INDEX/KEY column-list surfaces the columns
 * of the table being created (the table is not in the snapshot yet). Segments
 * leading with a table-constraint / index keyword are skipped; backtick- /
 * double-quoted ids are unquoted (case preserved) via the shared helpers.
 */
function parseMysqlInProgressCreateTableColumns(
  text: string,
  statementStart: number,
  cursorOffset: number,
  /** `excludeGenerated` drops `GENERATED ALWAYS AS (…)` / `AS (…) STORED`
   *  computed columns — illegal operands inside another column's generation
   *  expression (mysql script.44 §2). */
  opts?: { excludeGenerated?: boolean },
): string[] {
  const slice = text.slice(statementStart, cursorOffset);
  const m = MYSQL_CREATE_TABLE_HEADER_RE.exec(slice);
  if (!m) return [];
  const body = slice.slice(m.index + m[0].length);
  const segments: string[] = [];
  let depth = 0;
  let segStart = 0;
  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i]!;
    if (ch === '(') depth += 1;
    else if (ch === ')') depth -= 1;
    else if (ch === ',' && depth === 0) {
      segments.push(body.slice(segStart, i));
      segStart = i + 1;
    }
  }
  segments.push(body.slice(segStart));

  const out: string[] = [];
  const seen = new Set<string>();
  for (const seg of segments) {
    const im = MYSQL_FIRST_IDENT_RE.exec(seg);
    if (!im || !im[1]) continue;
    const raw = unquoteIdentifier(im[1]);
    if (MYSQL_TABLE_CONSTRAINT_LEAD.has(raw.toLowerCase())) continue;
    if (opts?.excludeGenerated && /\bGENERATED\b|\bAS\s*\(/i.test(seg)) continue;
    const key = foldIdentifier(im[1]);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(raw);
  }
  return out;
}

/**
 * GRA8-ROLL-4 — emit the curated MySQL data type list at a "data-type" slot
 * (currently the CREATE PROCEDURE/FUNCTION parameter type slot the
 * EditorContext slot seam classifies). Tier 1 (dominant) so the type
 * vocabulary tops the suggestion list at the type slot. Kind is `keyword`
 * because the completion taxonomy renders SQL data types as keyword-shaped
 * tokens (no dedicated `data-type` kind exists), matching
 * `emitMssqlDataTypeCandidates`.
 */
/** `CAST(x AS ▮)` / `CONVERT(x, ▮)` — MySQL's cast-target vocabulary, which
 *  is not the column-declaration list (`SIGNED`, not `INT`). */
function emitMysqlCastTargetCandidates(out: CompletionCandidate[]): void {
  // Curated in usage order — the integer and string targets first — carried as
  // an `orderHint` so the ranker keeps it instead of sorting alphabetically.
  getMysqlCastTargetTypes().forEach((type, index) => {
    out.push({
      label: type,
      insertText: type,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'MySQL cast target',
      orderHint: index,
    });
  });
}

function emitMysqlDataTypeCandidates(out: CompletionCandidate[]): void {
  for (const type of getMysqlDataTypes()) {
    out.push({
      label: type,
      insertText: type,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'MySQL data type',
    });
  }
}

/**
 * F5.3 Wave-4 — emit in-progress ADD COLUMN names as tier-1 column candidates.
 * Deduplication against the snapshot-derived columns happens at the consumer's
 * filter; pushing duplicates is harmless because the post-filter dedups by
 * label.
 */
function emitMysqlInProgressAddedColumns(
  out: CompletionCandidate[],
  names: ReadonlyArray<string>,
): void {
  for (const n of names) {
    out.push({
      label: n,
      insertText: formatIdentifier(n),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: 'in-progress ADD COLUMN',
    });
  }
}

/**
 * F5.3 Wave-4 — emit columns of a specific target table at tier 1. Used by
 * the `ddl-index-column` and `ddl-references-column-list` slots. The lookup
 * narrows by `target.database` when explicit; otherwise it falls back to the
 * `currentDatabase` (the snapshot is per-database already).
 */
function emitMysqlTargetTableColumns(
  out: CompletionCandidate[],
  target: { database: string | null; name: string },
  tables: ReadonlyArray<SnapshotTable>,
  currentDatabase: string | undefined,
  opts?: { pkFirst?: boolean },
): void {
  const targetDb = target.database ?? (currentDatabase ? currentDatabase.toLowerCase() : null);
  const inDb = (t: SnapshotTable) => {
    if (!targetDb) return true;
    const tDb = (t.database ?? '').toLowerCase();
    return tDb === '' || tDb === targetDb;
  };
  // The written spelling first (`lower_case_table_names = 0` keeps `Orders`
  // and `orders` apart), else the case-insensitive match (the other settings
  // store names lower-cased) — the rule `mysqlTableResolver` applies to FROM.
  const folded = target.name.toLowerCase();
  const tbl =
    tables.find((t) => t.name === target.name && inDb(t)) ??
    tables.find((t) => t.name.toLowerCase() === folded && inDb(t));
  if (!tbl) return;
  for (const c of tbl.columns) {
    // pkFirst (REFERENCES / constraint column-lists): the referenced table's
    // PK is what an FK points at in almost every case — keep it at tier 1
    // and drop the rest to tier 2 (mirrors the MSSQL resolver).
    const pk = c.isPrimaryKey === true;
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: opts?.pkFirst && !pk ? 2 : 1,
      detail: opts?.pkFirst && pk ? `${c.dataType} (PK, ${tbl.name})` : `${c.dataType} (${tbl.name})`,
    });
  }
}

/**
 * Emit FK-driven JOIN snippet candidates. For each candidate table in the
 * snapshot that has at least one FK edge to/from a bound alias, generate
 * one snippet per applicable edge. The ranker promotes `fkJoin: true`
 * candidates to tier 1 when `positionKind === 'from'` and at least one
 * alias is bound; outside that context they fall to tier 6 so misplaced
 * snippets cannot outrank real columns or relations. Mirrors the MSSQL /
 * PG resolver shape.
 *
 * `atJoinTargetSlot` discriminates a JOIN target slot (`… JOIN |` / `…
 * INNER JOIN |` / `… LATERAL |`) from a bare-FROM-list slot (`FROM |x` /
 * `FROM a, |`). At the latter, the emitted ON-clause is not a valid
 * syntactic continuation, so the candidates are demoted to tier 7 and
 * have `fkJoin: false` (so the ranker's `fkJoinTier` promotion does NOT
 * lift them back up). They stay discoverable via fuzzy match but no
 * longer crowd out the plain table the user is actually typing.
 */
function emitFkJoinSnippets(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  tables: ReadonlyArray<SnapshotTable>,
  fks: readonly FkEdge[],
  atJoinTargetSlot: boolean,
  quoting: MysqlQuotingOptions,
): void {
  const existingAliases = new Set<string>(bindings.map((b) => b.alias.toLowerCase()));
  // Spellings kept in the dedupe key: with `lower_case_table_names = 0`,
  // `Orders` and `orders` are two tables (two candidates).
  const seen = new Set<string>();
  // Relation identity (`fkEndpointMatch.ts`): each FK edge endpoint names its
  // exact catalog table — `Orders` never takes the FKs of `orders`.
  const match = makeFkEndpointMatcher(tables, mysqlBindingResolver(tables, quoting));
  // One index per pass — never cached across requests (`fkEdgeIndex.ts`).
  const applicableFks = makeApplicableFksFinder(fks, match);
  for (const binding of bindings) {
    if (binding.kind === 'cte' || binding.kind === 'subquery' || binding.kind === 'derived') {
      continue;
    }
    for (const t of tables) {
      const candidate: TableMeta = {
        name: t.name,
        schema: t.schema,
        database: t.database,
        kind: (t.kind === 'view' ? 'view' : 'table') as TableMeta['kind'],
        columns: [],
      };
      const applicable = applicableFks(binding, candidate);
      for (const fk of applicable) {
        const dedupeKey = `${binding.alias.toLowerCase()}|${t.database ?? ''}.${t.name}|${fk.name ?? `${fk.fromTable}->${fk.toTable}`}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        const snippet = generateFkJoinSnippet({
          from: binding,
          candidate,
          fk,
          existingAliases,
          engine: ENGINE,
          match,
        });
        out.push(
          atJoinTargetSlot
            ? snippet
            : { ...snippet, tier: 7, fkJoin: false },
        );
      }
    }
  }
}

function emitAllBoundAliasColumns(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  tables: ReadonlyArray<SnapshotTable>,
  cteBindings: readonly CteBinding[],
  quoting: MysqlQuotingOptions,
  cteSupported: boolean,
  fulltextFilter: ReadonlyMap<string, ReadonlySet<string>> | null,
  text: string,
): void {
  for (const b of bindings) {
    emitAliasColumns(out, b, tables, cteBindings, quoting, cteSupported, fulltextFilter, text);
  }
}

/**
 * I2 parity — the column set treated as JSON at a `JSON_EXTRACT(p.▮` slot:
 * buffer-affinity (columns this buffer already uses as a JSON-fn first arg)
 * ∪ the binding's bus-classified JSON columns. Lower-cased — the affinity
 * module compares case-insensitively. MetaInfo-declared columns are omitted
 * (BuildArgs carries no connectionId); the buffer signal covers the common
 * in-query case.
 */
function collectMysqlKnownJsonColumns(
  args: BuildArgs,
  binding: AliasBinding,
): Set<string> {
  const cols = collectBufferJsonFunctionColumns(args.text, MYSQL_JSON_FN_NAMES);
  const dbFolded = binding.database
    ? foldIdentifier(binding.database, args.quoting)
    : binding.schema
      ? foldIdentifier(binding.schema, args.quoting)
      : null;
  const tbl = resolveRelation(
    args.snapshotTables, foldIdentifier(binding.relation, args.quoting), dbFolded,
  );
  if (!tbl) return cols;
  for (const c of tbl.columns) {
    if (c.jsonClassification && c.jsonClassification.kind !== 'not-json') {
      cols.add(c.name.toLowerCase());
    }
  }
  return cols;
}

function emitAliasColumns(
  out: CompletionCandidate[],
  binding: AliasBinding,
  tables: ReadonlyArray<SnapshotTable>,
  cteBindings: readonly CteBinding[],
  quoting: MysqlQuotingOptions,
  cteSupported: boolean,
  fulltextFilter: ReadonlyMap<string, ReadonlySet<string>> | null = null,
  text: string = '',
): void {
  if (binding.kind === 'subquery' || binding.kind === 'derived') {
    // JSON_TABLE writer-declared COLUMNS (`JSON_TABLE(…) AS t`) — the synthetic
    // column names ARE the relation's projection. Surface them directly;
    // subquery-body inference would find nothing (the args are not a SELECT).
    if (binding.syntheticColumns && binding.syntheticColumns.length > 0) {
      for (const name of binding.syntheticColumns) {
        out.push({
          label: name,
          insertText: name,
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `(${binding.alias})`,
        });
      }
      return;
    }
    if (binding.bodyRange && text.length > 0) {
      const inferred = inferSubqueryColumns({
        source: text,
        bodyRange: binding.bodyRange,
        engineId: 'mysql',
        snapshotTables: tables,
        cteBindings,
      });
      for (const name of inferred) {
        out.push({
          label: name,
          insertText: name,
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `(${binding.alias})`,
        });
      }
    }
    return;
  }
  if (binding.kind === 'cte') {
    if (!cteSupported) return;
    const relationFolded = foldIdentifier(binding.relation, quoting);
    const cte = cteBindings.find((c) => foldIdentifier(c.name, quoting) === relationFolded);
    if (!cte) return;
    emitCteColumnList(
      out,
      cte,
      { source: text, engineId: 'mysql', snapshotTables: tables, cteBindings },
      binding.alias,
    );
    return;
  }
  // Resolve the relation against the snapshot. If the binding has an
  // explicit database qualifier, match it; otherwise pick the first
  // matching name (the snapshot only carries the current DB's tables, so
  // the unqualified case is unambiguous in practice).
  const relationFolded = foldIdentifier(binding.relation, quoting);
  const dbFolded = binding.database
    ? foldIdentifier(binding.database, quoting)
    : binding.schema
      ? foldIdentifier(binding.schema, quoting)
      : null;
  const tbl = resolveRelation(tables, relationFolded, dbFolded);
  if (tbl) {
    // FULLTEXT restriction (MATCH AGAINST context). When present, only
    // columns covered by a FULLTEXT index on the resolved relation surface.
    const allowedCols = fulltextFilter
      ? fulltextFilter.get(tbl.name.toLowerCase()) ?? new Set<string>()
      : null;

    for (const c of tbl.columns) {
      if (allowedCols && !allowedCols.has(c.name.toLowerCase())) continue;
      out.push({
        label: c.name,
        insertText: formatIdentifier(c.name),
        isSnippet: false,
        kind: 'column',
        tier: 1,
        detail: allowedCols
          ? `${c.dataType} (${binding.alias}) — FULLTEXT`
          : `${c.dataType} (${binding.alias})`,
      });
    }
    return;
  }

  // F5.3 Wave-4 — Virtual system-database tables. MySQL's `information_schema`
  // and `mysql` system databases aren't carried in the bus snapshot. Surface
  // documented column lists when an alias resolves to one of them. Closes
  // goals/autocomplete-coverage mysql script.19 §1, §4 and script.20 §1, §3.
  const sysKey = dbFolded ? `${dbFolded}.${relationFolded}` : null;
  const sysCols = sysKey ? MYSQL_SYSTEM_TABLE_COLUMNS[sysKey] : undefined;
  if (sysCols) {
    for (const colName of sysCols) {
      out.push({
        label: colName,
        insertText: formatIdentifier(colName),
        isSnippet: false,
        kind: 'column',
        tier: 1,
        detail: `(${binding.alias})`,
      });
    }
  }
}

/**
 * F5.3 Wave-4 — MySQL virtual system-database tables. Each entry maps a
 * fully-qualified `database.table` (lower-case) to its documented column
 * list. Not in the schema-bus snapshot; this static map keeps alias-dot
 * completion working for cross-DB system queries. Coverage focused on the
 * tables exercised by autocomplete-coverage scripts 19 and 20; extend as
 * other system tables get added to coverage.
 */
/**
 * F5.3 Wave-4 — MySQL system-schema table lists. Surfaced when the user
 * types `<schema>.|` at a dotted-access position, e.g. `information_schema.|`
 * or `mysql.|`. Uppercase casing for information_schema matches MySQL's
 * documented convention; mysql.* uses the canonical lowercase table names.
 */
const MYSQL_SYSTEM_SCHEMA_TABLES: Readonly<Record<string, readonly string[]>> = {
  information_schema: [
    'TABLES', 'COLUMNS', 'VIEWS', 'ROUTINES', 'PARAMETERS',
    'KEY_COLUMN_USAGE', 'TABLE_CONSTRAINTS', 'REFERENTIAL_CONSTRAINTS',
    'SCHEMATA', 'STATISTICS', 'PARTITIONS', 'CHECK_CONSTRAINTS',
    'TRIGGERS', 'EVENTS', 'COLUMN_PRIVILEGES', 'TABLE_PRIVILEGES',
    'USER_PRIVILEGES', 'SCHEMA_PRIVILEGES', 'CHARACTER_SETS',
    'COLLATIONS', 'ENGINES', 'PLUGINS', 'PROCESSLIST',
    'INNODB_TABLES', 'INNODB_INDEXES', 'INNODB_BUFFER_POOL_STATS',
  ],
  mysql: [
    'user', 'db', 'tables_priv', 'columns_priv', 'proxies_priv',
    'role_edges', 'default_roles', 'global_grants',
    'time_zone', 'time_zone_name', 'time_zone_transition',
    'time_zone_transition_type', 'time_zone_leap_second',
    'help_topic', 'help_category', 'help_keyword', 'help_relation',
    'general_log', 'slow_log', 'plugin', 'servers',
    'innodb_table_stats', 'innodb_index_stats',
  ],
  performance_schema: [
    'events_waits_current', 'events_waits_history',
    'events_statements_current', 'events_statements_history',
    'threads', 'mutex_instances', 'rwlock_instances',
    'file_summary_by_event_name', 'table_io_waits_summary_by_table',
    'replication_connection_status', 'replication_applier_status',
  ],
  sys: [
    'host_summary', 'host_summary_by_file_io',
    'host_summary_by_statement_latency',
    'innodb_buffer_stats_by_schema', 'innodb_buffer_stats_by_table',
    'metrics', 'processlist', 'session', 'statement_analysis',
    'schema_table_statistics', 'schema_index_statistics',
    'user_summary', 'version', 'waits_by_host_by_latency',
  ],
};

const MYSQL_SYSTEM_TABLE_COLUMNS: Readonly<Record<string, readonly string[]>> = {
  // information_schema — SQL standard introspection.
  'information_schema.tables': [
    'table_catalog', 'table_schema', 'table_name', 'table_type',
    'engine', 'version', 'row_format', 'table_rows', 'avg_row_length',
    'data_length', 'max_data_length', 'index_length', 'data_free',
    'auto_increment', 'create_time', 'update_time', 'check_time',
    'table_collation', 'checksum', 'create_options', 'table_comment',
  ],
  'information_schema.columns': [
    'table_catalog', 'table_schema', 'table_name', 'column_name',
    'ordinal_position', 'column_default', 'is_nullable', 'data_type',
    'character_maximum_length', 'character_octet_length',
    'numeric_precision', 'numeric_scale', 'datetime_precision',
    'character_set_name', 'collation_name', 'column_type', 'column_key',
    'extra', 'privileges', 'column_comment', 'is_generated',
    'generation_expression', 'srs_id',
  ],
  'information_schema.views': [
    'table_catalog', 'table_schema', 'table_name', 'view_definition',
    'check_option', 'is_updatable', 'definer', 'security_type',
    'character_set_client', 'collation_connection',
  ],
  'information_schema.key_column_usage': [
    'constraint_catalog', 'constraint_schema', 'constraint_name',
    'table_catalog', 'table_schema', 'table_name', 'column_name',
    'ordinal_position', 'position_in_unique_constraint',
    'referenced_table_schema', 'referenced_table_name',
    'referenced_column_name',
  ],
  'information_schema.table_constraints': [
    'constraint_catalog', 'constraint_schema', 'constraint_name',
    'table_schema', 'table_name', 'constraint_type', 'enforced',
  ],
  'information_schema.referential_constraints': [
    'constraint_catalog', 'constraint_schema', 'constraint_name',
    'unique_constraint_catalog', 'unique_constraint_schema',
    'unique_constraint_name', 'match_option', 'update_rule', 'delete_rule',
    'table_name', 'referenced_table_name',
  ],
  'information_schema.routines': [
    'specific_name', 'routine_catalog', 'routine_schema', 'routine_name',
    'routine_type', 'data_type', 'routine_body', 'routine_definition',
    'external_language', 'is_deterministic', 'sql_data_access',
    'security_type', 'created', 'last_altered', 'definer', 'character_set_client',
  ],
  // mysql.* — system-database privilege / metadata tables.
  'mysql.user': [
    'Host', 'User', 'Select_priv', 'Insert_priv', 'Update_priv',
    'Delete_priv', 'Create_priv', 'Drop_priv', 'Reload_priv', 'Shutdown_priv',
    'Process_priv', 'File_priv', 'Grant_priv', 'References_priv',
    'Index_priv', 'Alter_priv', 'Show_db_priv', 'Super_priv',
    'Create_tmp_table_priv', 'Lock_tables_priv', 'Execute_priv',
    'Repl_slave_priv', 'Repl_client_priv', 'Create_view_priv',
    'Show_view_priv', 'Create_routine_priv', 'Alter_routine_priv',
    'Create_user_priv', 'Event_priv', 'Trigger_priv', 'plugin',
    'authentication_string', 'password_expired', 'password_last_changed',
    'password_lifetime', 'account_locked', 'Create_role_priv',
    'Drop_role_priv', 'Password_reuse_history', 'Password_reuse_time',
    'Password_require_current', 'User_attributes',
  ],
  'mysql.db': [
    'Host', 'Db', 'User', 'Select_priv', 'Insert_priv', 'Update_priv',
    'Delete_priv', 'Create_priv', 'Drop_priv', 'Grant_priv',
    'References_priv', 'Index_priv', 'Alter_priv', 'Create_tmp_table_priv',
    'Lock_tables_priv', 'Create_view_priv', 'Show_view_priv',
    'Create_routine_priv', 'Alter_routine_priv', 'Execute_priv',
    'Event_priv', 'Trigger_priv',
  ],
  'mysql.tables_priv': [
    'Host', 'Db', 'User', 'Table_name', 'Grantor', 'Timestamp',
    'Table_priv', 'Column_priv',
  ],
  'mysql.columns_priv': [
    'Host', 'Db', 'User', 'Table_name', 'Column_name', 'Timestamp',
    'Column_priv',
  ],
  'mysql.proxies_priv': [
    'Host', 'User', 'Proxied_host', 'Proxied_user', 'With_grant',
    'Grantor', 'Timestamp',
  ],
  'mysql.role_edges': [
    'FROM_HOST', 'FROM_USER', 'TO_HOST', 'TO_USER', 'WITH_ADMIN_OPTION',
  ],
  'mysql.default_roles': [
    'HOST', 'USER', 'DEFAULT_ROLE_HOST', 'DEFAULT_ROLE_USER',
  ],
  'mysql.global_grants': [
    'USER', 'HOST', 'PRIV', 'WITH_GRANT_OPTION',
  ],
  'mysql.time_zone': [
    'Time_zone_id', 'Use_leap_seconds',
  ],
  'mysql.time_zone_name': [
    'Name', 'Time_zone_id',
  ],
};

/**
 * Resolve a folded relation name to a snapshot table. When the binding
 * explicitly qualifies the database, match `(database, name)`. Otherwise
 * match any name (snapshot only carries one DB's tables).
 */
/**
 * The table a relation reference (`orders`, `` `sakila`.`Orders` ``) names, as
 * MySQL resolves it: the snapshot holds the current database's tables. With
 * `lower_case_table_names = 0` a table name is case-sensitive, so the exact
 * (unquoted) spelling wins; otherwise the folded match (`resolveRelation`).
 */
function mysqlTableResolver(
  tables: ReadonlyArray<SnapshotTable>,
  quoting: MysqlQuotingOptions,
): RelationResolver<SnapshotTable> {
  return (name, schema) => {
    const dbFolded = schema ? foldIdentifier(schema, quoting) : null;
    const raw = unquoteIdentifier(name, quoting);
    return (
      tables.find((t) => t.name === raw && (!dbFolded || (t.database ?? '').toLowerCase() === dbFolded)) ??
      resolveRelation(tables, foldIdentifier(name, quoting), dbFolded)
    );
  };
}

/** The table a bound relation names, as MySQL resolves it — FK / loose-FK JOIN identity. */
function mysqlBindingResolver(
  tables: ReadonlyArray<SnapshotTable>,
  quoting: MysqlQuotingOptions,
): (binding: AliasBinding) => SnapshotTable | undefined {
  const resolve = mysqlTableResolver(tables, quoting);
  return (b) => resolve(b.relation, b.database || b.schema || undefined);
}

function resolveRelation(
  tables: ReadonlyArray<SnapshotTable>,
  relationFolded: string,
  dbFolded: string | null,
): SnapshotTable | undefined {
  if (dbFolded) {
    return tables.find(
      (t) =>
        t.name.toLowerCase() === relationFolded &&
        (t.database ?? '').toLowerCase() === dbFolded,
    );
  }
  return tables.find((t) => t.name.toLowerCase() === relationFolded);
}

type CteResolveArgs = {
  readonly source: string;
  readonly engineId: DatabaseEngineId;
  readonly snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly cteBindings: readonly CteBinding[];
};

function emitCteColumnList(
  out: CompletionCandidate[],
  cte: CteBinding,
  resolveArgs: CteResolveArgs,
  aliasOrCteName?: string,
): void {
  const cols = resolveCteColumns({ cte, ...resolveArgs });
  if (cols.length === 0) return;
  const detailContext = aliasOrCteName ?? cte.name;
  for (const col of cols) {
    out.push({
      label: col,
      insertText: col,
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `Column (${detailContext})`,
    });
  }
}

/**
 * Emit columns of the INSERT target table (referenced via the row alias
 * named in `INSERT … AS new …`). The columns surface at tier 1 because in
 * the `ON DUPLICATE KEY UPDATE` body they're the most likely completion.
 */
function emitInsertNewAliasColumns(
  out: CompletionCandidate[],
  newAlias: MysqlInsertNewAlias,
  tables: ReadonlyArray<SnapshotTable>,
  quoting: MysqlQuotingOptions,
): void {
  const targetFolded = foldIdentifier(newAlias.name, quoting);
  const dbFolded = newAlias.database ? foldIdentifier(newAlias.database, quoting) : null;
  const tbl = resolveRelation(tables, targetFolded, dbFolded);
  if (!tbl) return;
  for (const c of tbl.columns) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${newAlias.alias})`,
    });
  }
}

/**
 * F5.3-R-bucket — surface columns of the bare INSERT target (no row alias).
 * Fires at `values` / `set` positions only; sibling to
 * `emitInsertNewAliasColumns` which serves the `INSERT … AS new` form.
 *
 * Tier 1 mirrors the new-alias path so the INSERT column-list parens and
 * the `ON DUPLICATE KEY UPDATE` LHS show the target columns ahead of
 * cross-database tables.
 */
function emitInsertTargetColumns(
  out: CompletionCandidate[],
  target: MysqlInsertTarget,
  tables: ReadonlyArray<SnapshotTable>,
  quoting: MysqlQuotingOptions,
): void {
  const targetFolded = foldIdentifier(target.name, quoting);
  const dbFolded = target.database ? foldIdentifier(target.database, quoting) : null;
  const tbl = resolveRelation(tables, targetFolded, dbFolded);
  if (!tbl) return;
  for (const c of tbl.columns) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${target.name})`,
    });
  }
}

/**
 * F5.2-CB2 — Emit the CREATE TRIGGER host table's columns under the
 * synthetic `NEW` / `OLD` pseudo-relation. Distinct from `newAlias` above:
 * that surfaces `INSERT … AS new ON DUPLICATE KEY UPDATE` row-alias columns;
 * this surfaces the trigger-target row columns inside a trigger body.
 */
function emitMysqlTriggerHostColumns(
  out: CompletionCandidate[],
  host: TriggerHostTable,
  tables: ReadonlyArray<SnapshotTable>,
  pseudo: 'new' | 'old',
  quoting: MysqlQuotingOptions,
): void {
  const nameLower = host.name.toLowerCase();
  const schemaLower = host.schema ? host.schema.toLowerCase() : undefined;
  const match = tables.find((t) => {
    if (t.name.toLowerCase() !== nameLower) return false;
    if (schemaLower) {
      // MySQL uses 'database' (schema is an alias). Match either.
      const dbOrSchema = ((t.database ?? '') || (t.schema ?? '')).toLowerCase();
      return dbOrSchema === schemaLower;
    }
    return true;
  });
  if (!match) return;
  const pseudoLabel = pseudo === 'new' ? 'NEW' : 'OLD';
  // quoting consumed to keep parity with sibling emitters even if not used.
  void quoting;
  for (const c of match.columns) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${pseudoLabel} → ${match.name})`,
    });
  }
}

/**
 * JCS Slice C3 — type-aware JSON operator emission for MySQL. Walks back
 * from the cursor to find the preceding column operand; if it resolves to
 * a JSON-classified column (`native-json`), emits the dialect-correct JSON
 * operator list (`->`, `->>`, `JSON_CONTAINS`, `JSON_OVERLAPS`, `MEMBER OF`,
 * etc.) via SR-RO's `suggestOperatorsForOperand`. Numeric / text /
 * unclassified columns yield no JSON operator candidates — strictly more
 * conservative than the v0 unconditional `emitJsonOperators` it replaces.
 */
function emitTypeAwareJsonOperators(out: CompletionCandidate[], args: BuildArgs): void {
  const operand = findPrecedingColumnOperand({
    text: args.text,
    offset: args.ctx.offset,
    statementStart: args.ctx.statementRange.start,
    aliasBindings: args.ctx.aliasBindings,
    snapshotTables: args.snapshotTables,
    fold: (s) => foldIdentifier(s, args.quoting),
  });
  if (operand) {
    emitTypeAwareOperators(out, operand.jsonClassification, operand.column.dataType, 'mysql', args.ctx.positionKind);
    return;
  }
  // Slice 3a — typed expression operand (`CAST(x AS SIGNED) ▮`, `UPPER(c) ▮`).
  const exprType = findPrecedingExpressionType({
    text: args.text,
    offset: args.ctx.offset,
    statementStart: args.ctx.statementRange.start,
    engine: 'mysql',
    resolveRoutineReturnType: makeRoutineReturnResolver(args.snapshotRoutines, (s) => s.toLowerCase()),
  });
  if (exprType) emitTypeAwareExpressionOperators(out, exprType, 'mysql', args.ctx.positionKind);
}

/**
 * JCS Slice C4 — JSON path completion for MySQL. Mirrors PG's
 * `collectPgJsonPathCandidates`. Detects whether the cursor sits inside a
 * recognised JSON-path string (after `->`, `->>`, `JSON_VALUE(col, `,
 * `JSON_EXTRACT(col, `, etc.), resolves the LHS column reference against the
 * snapshot honouring FROM/JOIN aliases via the shared resolveColumnRefToTable
 * (bindings come from ctx.aliasBindings, a token-stream scan independent of
 * the tree-sitter tree), and emits sampled path hints from the per-session cache
 * when the column is JSON-classified.
 */
function collectMysqlJsonPathCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  quoting: MysqlQuotingOptions;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as MySQL resolves it (the current database). */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  const pos = findJsonPathStringPosition(args.text, args.offset);
  if (!pos) return [];
  // `PATH '…'` literal inside a JSON_TABLE COLUMNS body — the path is relative
  // to the synthetic row, so there is no LHS column to resolve. Offer at least
  // the syntactic root `$` as a starting target.
  if (pos.noColumn) {
    const rootOut: CompletionCandidate[] = [];
    emitJsonPathCandidates(rootOut, {
      engine: 'mysql',
      cacheEntry: undefined,
      declaredHints: undefined,
      positionKind: pos.kind,
      partial: pos.partial,
      offerSyntacticRootWhenEmpty: pos.rootFallback ?? false,
    });
    return rootOut;
  }
  const colSegment = readMysqlIdentifierBefore(args.text, pos.columnSideEnd);
  if (!colSegment) return [];
  const columnRef = colSegment.parts.join('.');
  const resolved = resolveColumnRefToTable(
    columnRef,
    args.aliasBindings,
    args.snapshotTables,
    (s) => foldIdentifier(s, args.quoting),
    args.resolveTable,
  );
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;
  const hintSchema = tbl.schema ?? tbl.database ?? '';
  // Offer JSON-path hints when the bus classified the column JSON
  // (engine-native / CHECK) OR the user declared it via MetaInfo
  // ("Treat as JSON" — a plain string column the schema bus can't know
  // about). The declaration source is invalidated by the Table Designer
  // toggle, so this reflects changes without a reconnect.
  const busJson =
    !!colMeta.jsonClassification &&
    colMeta.jsonClassification.kind !== 'not-json';
  if (
    !busJson &&
    !isJsonColumnDeclared(
      args.connectionId, args.database, hintSchema, tbl.name, colMeta.name,
    )
  ) {
    return [];
  }
  const cacheEntry = getPathHints(args.connectionId, args.database, {
    engine: 'mysql',
    schema: hintSchema,
    table: tbl.name,
    column: colMeta.name,
  });
  const declaredHints = getJsonPathHints(
    args.connectionId,
    args.database,
    hintSchema,
    tbl.name,
    colMeta.name,
  );
  const out: CompletionCandidate[] = [];
  emitJsonPathCandidates(out, {
    engine: 'mysql',
    cacheEntry,
    declaredHints,
    positionKind: pos.kind,
    partial: pos.partial,
    offerSyntacticRootWhenEmpty: pos.rootFallback ?? false,
  });
  return out;
}

/**
 * Phase 3 — enum-value completion for MySQL. Resolves the LHS column
 * reference against the snapshot honouring FROM/JOIN aliases via the shared
 * resolveColumnRefToTable (bindings come from ctx.aliasBindings, a
 * token-stream scan independent of the tree-sitter tree), keyed off a comparison
 * value position rather than a JSON-path string.
 *
 * Path-aware extension (MERGE-2): when the LHS of the comparison is a MySQL
 * JSON-extract expression (`col->>'$.k'`, `JSON_EXTRACT(col, '$.k')`, etc.),
 * the resolved canonical path is passed to `emitEnumValueCandidates` so the
 * path-keyed MetaInfo enum cache is consulted instead of the column-level
 * cache. Wildcard probe is handled inside `emitEnumValueCandidates`.
 */
function collectMysqlEnumValueCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  quoting: MysqlQuotingOptions;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as MySQL resolves it (the current database). */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  // Table DDL carries no queried relation, so a bare column reference here
  // must not fall back to an unrelated snapshot table's cached values
  // (see `isInsideTableDdlStatement`). Closes oracle script.29 §4.
  if (isInsideTableDdlStatement(args.text, args.offset)) return [];
  const cmp = detectComparisonValueContext(args.text, args.offset);
  if (!cmp) {
    // `FIND_IN_SET('<cursor>', set_col)` — the value literal is the FIRST
    // argument and the SET column is the SECOND (forward of the caret), which
    // detectComparisonValueContext's backward scan can't reach. Surface the
    // column's native SET members directly.
    const fisCol = detectFindInSetColumnArg(args.text, args.offset);
    if (fisCol) {
      const members = collectColumnNativeMembers(fisCol, args);
      if (members.length > 0) return members;
    }
    // detectComparisonValueContext misses JSON-chain LHS (col->>'$.k' = …,
    // JSON_EXTRACT(col, '$.k') = …) because the char immediately before `=`
    // is the closing quote of the path literal or the closing paren of the
    // function call — not part of an identifier. Try the MySQL JSON-path
    // extractor directly against each candidate column; the extractor's own
    // findComparisonOpEndBefore gate keeps this cheap — it returns null
    // immediately when the cursor isn't at a comparison RHS.
    return collectMysqlJsonChainEnumValueCandidates(args);
  }
  const resolved = resolveColumnRefToTable(
    cmp.columnRef,
    args.aliasBindings,
    args.snapshotTables,
    (s) => foldIdentifier(s, args.quoting),
    args.resolveTable,
  );
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;

  // Try to extract a JSON path from the cursor context (MySQL-specific).
  // `extractJsonPathFromCursorMysql` looks at the LHS of the comparison
  // operator for `->>` / `JSON_EXTRACT` / `JSON_VALUE` / `JSON_UNQUOTE(JSON_EXTRACT(...))`
  // expressions and returns the canonical path.
  const pathResult = extractJsonPathFromCursorMysql({
    text: args.text,
    cursorOffset: args.offset,
    column: colMeta.name,
  });

  const out: CompletionCandidate[] = [];
  emitEnumValueCandidates(out, {
    connectionId: args.connectionId,
    database: args.database,
    schema: tbl.schema ?? tbl.database ?? '',
    table: tbl.name,
    column: colMeta.name,
    // Pass the canonical path when a JSON-extract expression was detected.
    path: pathResult?.canonicalPath,
    quote: findValueStringBodyStart(args.text, args.offset) === undefined,
    // Native MySQL ENUM/SET members travel inline in COLUMN_TYPE, so surface
    // them synchronously when the column isn't a declared MetaInfo enum. Only
    // for the column-level lookup (a JSON-extract LHS column is `json`, whose
    // type carries no inline members — `parseInlineEnumMembers` returns null).
    nativeFallbackMembers: pathResult
      ? undefined
      : parseInlineEnumMembers(colMeta.dataType) ?? undefined,
  });
  return out;
}

/**
 * Resolve a (possibly aliased / dotted) column reference and emit its
 * enum/SET value candidates — declared MetaInfo values first, falling back to
 * the native inline `ENUM(...)` / `SET(...)` members. Shared by the
 * `FIND_IN_SET` slot (the column is a forward argument) so it reuses the same
 * value sourcing as the comparison-operator path.
 */
function collectColumnNativeMembers(
  columnRef: string,
  args: {
    text: string;
    offset: number;
    connectionId: string;
    database: string;
    snapshotTables: ReadonlyArray<SnapshotTable>;
    quoting: MysqlQuotingOptions;
    readonly aliasBindings: readonly AliasBinding[];
    readonly resolveTable: RelationResolver<SnapshotTable>;
  },
): CompletionCandidate[] {
  const resolved = resolveColumnRefToTable(
    columnRef,
    args.aliasBindings,
    args.snapshotTables,
    (s) => foldIdentifier(s, args.quoting),
    args.resolveTable,
  );
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;
  const out: CompletionCandidate[] = [];
  emitEnumValueCandidates(out, {
    connectionId: args.connectionId,
    database: args.database,
    schema: tbl.schema ?? tbl.database ?? '',
    table: tbl.name,
    column: colMeta.name,
    quote: findValueStringBodyStart(args.text, args.offset) === undefined,
    nativeFallbackMembers: parseInlineEnumMembers(colMeta.dataType) ?? undefined,
  });
  return out;
}

/**
 * Fallback for JSON-chain LHS comparisons that `detectComparisonValueContext`
 * misses (e.g. `col->>'$.k' = '|'`, `JSON_EXTRACT(col, '$.k') = '|'`).
 * Iterates over every column visible via the snapshot tables, runs the MySQL
 * JSON-path extractor for each, and emits enum-value candidates for the
 * first match.
 *
 * The extractor's own `findComparisonOpEndBefore` gate makes this cheap: it
 * returns `null` immediately when the cursor is not at a comparison RHS, so
 * the loop exits fast in non-matching positions.
 */
function collectMysqlJsonChainEnumValueCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  quoting: MysqlQuotingOptions;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as MySQL resolves it (the current database). */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  const quote = findValueStringBodyStart(args.text, args.offset) === undefined;

  // The extractor's backward scan never depends on the column (it is only
  // compared at the end) — scan once, then match the column it names. This
  // fallback runs on nearly every request; one scan per snapshot column was
  // ~20% of provider time on a 600-table schema (1.5.1 F2).
  const pathResult = extractJsonPathFromCursorMysql({ text: args.text, cursorOffset: args.offset });
  if (!pathResult) return [];
  const targetColumn = pathResult.column.toLowerCase();
  // The statement's own relations (as MySQL resolves them) first, then the
  // first snapshot table owning the column.
  for (const tbl of boundTablesFirst(args.aliasBindings, args.snapshotTables, args.resolveTable)) {
    for (const col of tbl.columns) {
      if (col.name.toLowerCase() !== targetColumn) continue;

      // Found a JSON-chain match. Emit enum values for this column + path.
      const out: CompletionCandidate[] = [];
      emitEnumValueCandidates(out, {
        connectionId: args.connectionId,
        database: args.database,
        schema: tbl.schema ?? tbl.database ?? '',
        table: tbl.name,
        column: col.name,
        path: pathResult.canonicalPath,
        quote,
      });
      if (out.length > 0) return out;
    }
  }
  return [];
}

/**
 * MySQL-local fix for script-49-cp-3 (`IN ('PG', '|PG-13')` enum suggestion
 * drop-out). When the caret sits inside a CLOSED single-quoted string literal
 * — body still ahead, closing quote on the same line — widen the enum-value
 * replace range forward to cover the existing body so the bare value
 * insertion replaces (not prepends) the body. Without this, Monaco silently
 * drops the enum item whose label is a prefix of the surrounding word (see
 * the call-site for the full reasoning).
 *
 * Returns the input range unchanged when:
 *   - the caret is NOT at the body start of a closed literal (e.g. mid-body
 *     of an in-progress `'op|` literal — `computeEnumValueEdit` already
 *     produced a non-empty range covering the partial),
 *   - no closing `'` is found on the same line ahead of the cursor (the
 *     literal is still in-progress, the user is mid-typing),
 *   - or the input range is non-empty (caller already widened the range to
 *     cover existing prefix bytes — don't second-guess that decision).
 */
function maybeExtendEnumValueRangeForClosedLiteral(
  text: string,
  offset: number,
  range: MonacoRange,
  position: { readonly lineNumber: number; readonly column: number },
): MonacoRange {
  // Only consider single-line cursor positions — `findValueStringBodyStart`
  // returns undefined when a `\n` breaks the literal, so the caller would
  // not have reached this point with a multi-line range. Defensive check.
  if (range.startLineNumber !== range.endLineNumber) return range;
  // Only widen ZERO-width ranges (caret at body start of a closed literal).
  // A non-zero range means the user typed a partial (`'op|`) and the helper
  // already chose the correct replacement window — leave it alone.
  if (range.startColumn !== range.endColumn) return range;
  // Caret must immediately follow an opening `'` (i.e. cursor at body start
  // of SOME literal). Otherwise we're not in a string-value position at all.
  if (offset === 0 || text[offset - 1] !== "'") return range;
  // Scan forward from the cursor for the closing quote, treating doubled `''`
  // as an escaped quote (matches the convention used by
  // `findValueStringBodyStart` and `skipSingleQuoted`). A `\n` before the
  // closing quote means the literal is still in-progress (multi-line strings
  // are not supported in v1) — fall back to the zero-width range.
  let j = offset;
  while (j < text.length) {
    const ch = text[j]!;
    if (ch === '\n') return range; // unclosed literal — leave as-is
    if (ch === "'") {
      if (text[j + 1] === "'") {
        j += 2; // escaped — keep scanning
        continue;
      }
      break; // closing quote found
    }
    j += 1;
  }
  if (j >= text.length) return range; // EOF without closing quote
  // `j` is the index of the closing `'`. Extend the end column to cover
  // every body character from the cursor to just before that quote.
  const extension = j - offset;
  if (extension <= 0) return range; // empty body (`''`) — nothing to widen
  return {
    startLineNumber: range.startLineNumber,
    startColumn: range.startColumn,
    endLineNumber: position.lineNumber,
    endColumn: range.endColumn + extension,
  };
}

type MonacoRange = {
  readonly startLineNumber: number;
  readonly startColumn: number;
  readonly endLineNumber: number;
  readonly endColumn: number;
};

/**
 * Read a 1–3-part dotted identifier ending at `offset`. Accepts bare,
 * backticked, and (when ANSI_QUOTES is on) double-quoted segments.
 */
function readMysqlIdentifierBefore(
  text: string,
  offset: number,
): { readonly parts: readonly string[] } | null {
  let i = offset;
  while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  const parts: string[] = [];
  while (i > 0) {
    const end = i;
    while (i > 0 && /[A-Za-z0-9_`"$]/.test(text[i - 1]!)) i -= 1;
    if (i === end) break;
    parts.unshift(text.slice(i, end));
    while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
    if (text[i - 1] !== '.') break;
    i -= 1;
    while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  }
  return parts.length > 0 ? { parts } : null;
}

/**
 * Emit `@varname` user-variable candidates. The `label` shows the leading
 * `@` for visibility; `insertText` and `filterText` strip it because Monaco
 * treats `@` as a non-word boundary — the typed prefix Monaco hands the
 * filter is the bare identifier following the `@`. Tier 5 (other) so user
 * vars rank between routines (3) and keywords (7).
 */
function emitUserVariables(out: CompletionCandidate[], userVars: readonly string[]): void {
  for (const name of userVars) {
    out.push({
      label: `@${name}`,
      insertText: name,
      isSnippet: false,
      kind: 'variable',
      tier: 5,
      detail: 'User variable',
      filterText: name,
    });
  }
}

/**
 * Emit `@@global.x` / `@@session.x` system-variable candidates. Same
 * label/insertText asymmetry as user vars — Monaco's word boundary lands
 * after `@@`, so the typed prefix is the bare variable name.
 */
function emitSystemVariables(out: CompletionCandidate[], mariadb: boolean): void {
  for (const v of getMysqlSystemVariables({ mariadb })) {
    out.push({
      label: `@@${v.name}`,
      insertText: v.name,
      isSnippet: false,
      kind: 'variable',
      tier: 8,
      detail: v.description,
      filterText: v.name,
    });
  }
}

/**
 * Detect whether the cursor is preceded by `@` or `@@` (i.e. the user is
 * in the middle of typing a variable reference). Walks backward past any
 * partial bare-identifier prefix, then checks the leading char(s).
 */
function detectAtVarPrefix(source: string, offset: number): 'user' | 'system' | null {
  let i = offset - 1;
  // Walk back over partial identifier chars.
  while (i >= 0 && /[A-Za-z0-9_$]/.test(source[i]!)) i -= 1;
  if (i < 0) return null;
  if (source[i] !== '@') return null;
  // Check for double-@ (system var).
  if (i - 1 >= 0 && source[i - 1] === '@') return 'system';
  return 'user';
}

/**
 * Build a FULLTEXT-index lookup from the native MySQL cache. Returns a
 * map of lower-cased table name → set of lower-cased column names that
 * are covered by at least one FULLTEXT index on that table. Empty map
 * when the cache is cold or carries no indexes.
 */
function buildFulltextIndex(
  native:
    | {
        readonly tables?: ReadonlyArray<{
          readonly name: string;
          readonly indexes?: ReadonlyArray<{
            readonly indexType?: string;
            readonly columns: readonly string[];
          }>;
        }>;
      }
    | undefined,
): ReadonlyMap<string, ReadonlySet<string>> {
  const out = new Map<string, Set<string>>();
  for (const t of native?.tables ?? []) {
    const cols = new Set<string>();
    for (const idx of t.indexes ?? []) {
      if ((idx.indexType ?? '').toUpperCase() !== 'FULLTEXT') continue;
      for (const c of idx.columns) cols.add(c.toLowerCase());
    }
    if (cols.size > 0) out.set(t.name.toLowerCase(), cols);
  }
  return out;
}

// Test-only export so unit tests can exercise the schema-aware emission
// without standing up the full Monaco translation.
export function __testEmitTablesAndDatabases(
  tables: ReadonlyArray<SnapshotTable>,
  databases: readonly string[],
  currentDatabase: string | undefined,
): CompletionCandidate[] {
  const out: CompletionCandidate[] = [];
  emitTablesAndDatabases(out, tables, databases, currentDatabase);
  return out;
}

export function __testNeedsBacktickQuote(s: string): boolean {
  return needsBacktickQuote(s);
}

export function __testBacktickQuote(s: string): string {
  return backtickQuote(s);
}

export function __testUnquote(s: string, opts?: MysqlQuotingOptions): string {
  return unquoteIdentifier(s, opts);
}

